import React, { useState, useEffect, useRef, useMemo } from 'react';
import { signOut, getSession } from '../data/auth';
import { countPending } from '../data/local/outbox';
import {
  fetchProfile, setActiveLaboratory, fetchActiveMemberships,
  fetchActiveLaboratories, listAllLaboratories, createLaboratory, addLabMember,
} from '../data/labs';
import { nomeExibido, usuarioDeEmail } from '../data/usuario';
import {
  listClients, insertClient, updateClient, deleteClient, newClientId,
  locaisDoCliente, novoLocal, prepararCliente,
} from '../data/clients';
import {
  criarFatura, listInvoices, listInstallments, refaturar, refaturarEntrada, entradaSuperadaPelaFatura,
} from '../data/invoices';
import RecebimentoModal from './FIVSystem/modals/RecebimentoModal';
import { quitarServico, desfazerQuitacaoDoServico } from '../data/recebimentos';
import { entradaAEmitir, MOTIVO_DA_ENTRADA, contaDoServico, taxaDeEntrada, entradaParaServicoNovo } from '../utils/calculations/entrada';
import { oQueVaiComOServico, pesoDaExclusao } from '../utils/calculations/exclusaoDeServico';
import { ehConflito, conflitoAtrapalha } from '../data/conflitos';
import {
  listServices, insertService, updateService, deleteService, newServiceId,
  updateServiceBilling, updateServiceCosts, updateServiceObservacaoDeCobranca, updateServiceWorkflow,
} from '../data/services';
import { novoCadastro, validarCadastro, montarCadastro } from '../data/cadastroDeServico';
import LabManagement from './LabManagement';
import EstoqueTab from './FIVSystem/tabs/EstoqueTab';
import EstoqueDeInsumos from './FIVSystem/shared/EstoqueDeInsumos';
import SyncBanner from './SyncBanner';
import Conflitos from './Conflitos';
import LicencaBloqueada from './LicencaBloqueada';
import { useLicenca } from '../hooks/useLicenca';
import { formaPorExtenso } from '../constants';
import { linhasDeCongelamento } from '../utils/calculations/congelamento';
import { linhasDosTransferidos, secaoDosTransferidos } from '../utils/transferidosNaFatura';
import { transferenciasComDiagnostico } from '../data/te';
import AssistenteTab from './FIVSystem/tabs/AssistenteTab';
import { assistenteLigado, perguntarAoAssistente } from '../data/assistente';
import {
  responder, proximosPassos, etapasConcluidasAgora, rotuloDaEtapa, cartaoDoServico,
  cartaoDoCliente, cartaoDoAnimal, porQueNaoCabe,
} from '../utils/assistente/fluxos';
import { searchAnimals, fetchAnimal } from '../data/animals';
import { listarDiagnosticosDePrenha } from '../data/relatorios';
import { encerrar, parada, lerConversaGuardada, guardarConversa } from '../utils/assistente/conversa';
import JanelaModal from './JanelaModal';
import EstoqueDoCliente from './FIVSystem/shared/EstoqueDoCliente';
import CruzamentosDoAnimal from './FIVSystem/modals/CruzamentosDoAnimal';
import { gerarFichaDeCampo } from '../utils/fichaDeCampo';
import { User, Activity, DollarSign, LogOut, Settings, ChevronDown, Building, Database, PawPrint, BarChart3, Snowflake, Package, MessageCircle } from 'lucide-react';

// Constantes
import { BILLING_MODELS, MARK, rotuloDoServico, curtoDoServico } from '../constants';
import { describeError } from '../data/errors';
import { LOGO_URL } from '../utils/assets';
import {
  definirLaboratorioDoRelatorio, logoDoRelatorio, dadosDoLaboratorio, responsavelDoLaboratorio,
} from '../utils/laboratorioNoRelatorio';
import { urlDaLogo, precosParaServicoNovo } from '../data/configuracaoDoLaboratorio';

// Utils
import {
  calculateServiceValue,
  calculateOperationalCosts,
  getOperationalCostsDetails,
  getServiceStatus,
  isPregnancyCheckOverdue,
  getDaysOverdue,
  getDaysSinceBilled
} from '../utils';

// Modais
import OperationalCostsModal from './FIVSystem/modals/OperationalCostsModal';
import BillingModal from './FIVSystem/modals/BillingModal';
import WorkflowModal from './FIVSystem/modals/WorkflowModal';
import ServiceModal from './FIVSystem/modals/ServiceModal';
import ConfirmarExclusaoModal from './FIVSystem/modals/ConfirmarExclusaoModal';
import FinanceiroDoServicoModal from './FIVSystem/modals/FinanceiroDoServicoModal';

// Tabs
import ClientsTab from './FIVSystem/tabs/ClientsTab';
import DashboardTab from './FIVSystem/tabs/DashboardTab';
import FinancialTab from './FIVSystem/tabs/FinancialTab';
import AnimalsTab from './FIVSystem/tabs/AnimalsTab';
import LogsTab from './FIVSystem/tabs/LogsTab';
import RelatoriosTab from './FIVSystem/tabs/RelatoriosTab';

/**
 * Uma aba já visitada continua montada, apenas escondida.
 *
 * Antes, cada aba era renderizada de forma condicional, presa ao activeTab:
 * trocar de aba DESMONTAVA a anterior. Voltar remontava do zero — e como `cached()` é
 * rede-primeiro, cada volta virava uma ida ao servidor. A tela recarregava
 * inteira, e junto se perdia o que não mora em estado global: filtro digitado,
 * período escolhido, grupo expandido, posição de rolagem.
 *
 * A aba só é montada na PRIMEIRA vez que é aberta — abrir o app não carrega as
 * sete de uma vez. Depois disso ela fica no ar, escondida por `display:none`.
 *
 * Definido FORA do FIVSystem de propósito: declarado lá dentro, seria uma
 * função nova a cada render, e o React trata identidade diferente como
 * componente diferente — desmontaria tudo a cada tecla digitada, que é
 * exatamente o problema que este componente existe para resolver.
 *
 * Seguro porque nenhuma aba registra ouvinte global nem timer no nível dela: o
 * único `addEventListener` do conjunto vive dentro do balão do calendário, que
 * só existe enquanto está aberto. Se uma aba futura precisar de `setInterval`,
 * ela é que terá de pausar quando estiver escondida.
 */
const Painel = ({ id, activeTab, visitadas, children }) => {
  if (!visitadas.has(id)) return null;
  return (
    <div style={activeTab === id ? undefined : { display: 'none' }}>
      {children}
    </div>
  );
};

const FIVSystem = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  // Abas já abertas nesta sessão. Set e não array: a pergunta é sempre
  // "já visitou?", nunca "em que ordem".
  const [visitadas, setVisitadas] = useState(() => new Set(['dashboard']));

  // Conta as entradas em cada aba. A aba fica viva escondida, então o que se
  // abriu nela continuava aberto na volta; quem quer começar do zero a cada
  // visita lê este número.
  const [entradas, setEntradas] = useState({});

  /** Troca de aba e marca como visitada, para o Painel poder mantê-la viva. */
  const abrirAba = (id) => {
    setVisitadas(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
    if (id !== activeTab) setEntradas(prev => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    if (id === 'assistente') setSugestoesNaoVistas(0);
    setActiveTab(id);
  };
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  
  // Estados do sistema
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [profile, setProfile] = useState(null);
  const [currentLab, setCurrentLab] = useState(null);
  // A etapa em que o workflow deve abrir, quando quem abriu disse qual.
  const [etapaInicial, setEtapaInicial] = useState(null);
  const [userLabs, setUserLabs] = useState([]);
  const [userRole, setUserRole] = useState('viewer');
  const [formData, setFormData] = useState({});
  // O serviço que estava sendo lançado quando o usuário foi cadastrar a
  // fazenda dele. Guardado inteiro (formulário e item em edição) para voltar
  // exatamente onde parou, com a fazenda nova já escolhida.
  const [servicoEmEspera, setServicoEmEspera] = useState(null);
  // O último serviço criado, para o quadro rolar até a linha dele.
  const [servicoEmDestaque, setServicoEmDestaque] = useState(null);

  // O dinheiro de UM serviço. Irmão do modal de etapas, e é o par dele que
  // permite as abas de Serviços e de Financeiro saírem.
  const [servicoNoFinanceiro, setServicoNoFinanceiro] = useState(null);
  // O serviço que está na mira da exclusão. Guarda o serviço inteiro, e não o id:
  // o modal precisa do código para pedir que a pessoa o digite, e do cliente para
  // dizer de quem é.
  const [servicoParaExcluir, setServicoParaExcluir] = useState(null);
  // De qual serviço é cada fatura. Existe para a trilha de auditoria poder
  // levar de uma linha de parcela ao financeiro do serviço: a parcela guarda
  // `invoice_id`, e só a fatura sabe o `service_id`.
  const [servicoDaFatura, setServicoDaFatura] = useState({});
  const [servicoDaParcela, setServicoDaParcela] = useState({});

  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [systemState, setSystemState] = useState('loading');
  const [needsAccessCheck, setNeedsAccessCheck] = useState(true);
  const [firstLabName, setFirstLabName] = useState('');
  const [firstLabError, setFirstLabError] = useState('');

  const [showCostsModal, setShowCostsModal] = useState(false);
  const [selectedCostsService, setSelectedCostsService] = useState(null);

  // Estados do filtro de data

  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedBillingService, setSelectedBillingService] = useState(null);
  // Parcelas indexadas por serviço, do jeito que a tela Financeiro consome.
  const [parcelasPorServico, setParcelasPorServico] = useState({});
  // Quanto da entrada de cada serviço já entrou no caixa. É o que abate o
  // faturamento final — cobrado e não pago não abate.
  const [entradaPagaPorServico, setEntradaPagaPorServico] = useState({});
  // Contador de recargas das parcelas. O painel de saldo lê do banco por conta
  // própria e não tem como saber que uma fatura acabou de sair; isto avisa.
  const [versaoDasParcelas, setVersaoDasParcelas] = useState(0);

  /**
   * O CAIXA de cada serviço: o que entrou pela fatura do serviço, e quando vence
   * a próxima parcela. Só dinheiro e datas, que é o que o banco sabe.
   *
   * A CONTA é derivada disto mais o preço do cadastro, logo abaixo, e não vem
   * mais somada das faturas: quem dita o valor é o cadastro, e o cadastro mora
   * em `calculateServiceValue`, no cliente. Guardar a conta pronta aqui era
   * guardar uma resposta que envelhece assim que alguém corrige um preço.
   */
  const [caixaPorServico, setCaixaPorServico] = useState({});
  const [entradaEmitidaPorServico, setEntradaEmitidaPorServico] = useState({});
  // Ref, nao estado: recusar o convite nao precisa repintar a tela, e um
  // `setState` aqui reabriria a pergunta no render seguinte.
  const entradaRecusada = useRef(new Set());
  // Forma de pagamento combinada, indexada por serviço.
  const [formaPorServico, setFormaPorServico] = useState({});

  /**
   * A CONTA DE CADA SERVIÇO, derivada do cadastro e do caixa.
   *
   * Era somada das faturas e guardada em estado. O problema não era o custo, era
   * a verdade: o valor vinha do documento emitido, então corrigir um preço ou uma
   * quantidade no cadastro não mexia no que o sistema dizia que o cliente devia
   * — e só refaturar acertava. Agora o saldo é uma subtração: preço do cadastro
   * menos o que entrou.
   *
   * `useMemo` e não estado porque a conta depende de `services`, que recarrega
   * por caminhos próprios: guardá-la pronta era a garantia de mostrar a conta de
   * antes da última correção. O de `vence` continua vindo do banco, que é quem
   * sabe de parcela.
   */
  const contaPorServico = useMemo(() => Object.fromEntries(
    (services || []).map(service => {
      const caixa = caixaPorServico[service.id] || {};
      return [service.id, {
        ...contaDoServico({
          billed: !!service.billed,
          valorDoCadastro: calculateServiceValue(service),
          // `entradaEmitidaPorServico` é `null` quando a leitura falhou, e aí a
          // entrada não entra na conta: melhor faltar cobrança na tela do que
          // inventar uma que talvez não exista.
          entradaFaturada: entradaEmitidaPorServico?.[service.id]?.valor || 0,
          entradaPaga: entradaPagaPorServico[service.id] || 0,
          servicoPago: caixa.pagoServico || 0,
        }),
        vence: caixa.vence || null,
      }];
    }),
  ), [services, caixaPorServico, entradaEmitidaPorServico, entradaPagaPorServico]);
  // Parcela cujos recebimentos estao abertos. Guarda a parcela E o servico:
  // a tela mostra o cliente e o numero do servico no cabecalho.
  const [recebendo, setRecebendo] = useState(null);
  // Produção no grão da linha da FIV (doadora × touro). É a fonte dos
  // indicadores técnicos do Dashboard — a mesma que os Painéis usam.
  const [modoCobranca, setModoCobranca] = useState('servico');

  useEffect(() => {
    if (user && systemState === 'loading') {
      checkUserAccess();
    }
    // Pelo id: o objeto `user` do Supabase é recriado a cada renovação de
    // token, e reabrir a verificação de acesso por causa disso devolvia a
    // tela para "Verificando acesso do usuário" no meio do trabalho.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Os relatórios saem com a cara do laboratório ativo. Registrado aqui, e
  // não passado a cada gerador, porque os PDFs de etapa são chamados de
  // dezenas de lugares; a configuração salva troca `currentLab` e chega junto.
  useEffect(() => {
    definirLaboratorioDoRelatorio(currentLab
      ? { ...currentLab, logo_url: urlDaLogo(currentLab.logo_path) }
      : null);
  }, [currentLab]);

  // Carregar dados quando laboratório muda
  useEffect(() => {
    if (currentLab?.id && systemState === 'active') {
      loadLabData();
    }
  }, [currentLab?.id, systemState]);

  /**
   * O ASSISTENTE. Ligado por laboratório no contrato; desligado, nem o botão
   * aparece. A conversa troca junto com o laboratório: os serviços de um não
   * respondem pelo outro.
   */
  const [comAssistente, setComAssistente] = useState(false);
  const [conversa, setConversa] = useState([]);
  const [assistentePensando, setAssistentePensando] = useState(false);
  // Sugestões da cadeia que chegaram com a pessoa em outra aba: viram contador
  // na aba Assistente, em vez de arrancá-la de onde está.
  const [sugestoesNaoVistas, setSugestoesNaoVistas] = useState(0);
  // O serviço de que a conversa está falando: o último escolhido. "Refatura a
  // entrada", sem nome, vale para ele.
  const [servicoEmFoco, setServicoEmFoco] = useState(null);
  // As duas fichas que só o assistente abre daqui: o botijão de um cliente e
  // os cruzamentos de um animal (as abas abrem as suas por conta própria).
  const [estoqueDe, setEstoqueDe] = useState(null);
  const [cruzamentosDe, setCruzamentosDe] = useState(null);
  // A FILA: "emitir todas as entradas", um serviço por vez. Guarda a lista,
  // onde está e se o modal do item atual está aberto.
  const [fila, setFila] = useState(null);
  useEffect(() => {
    if (comAssistente) guardarConversa(currentLab?.id, conversa, servicoEmFoco);
  }, [comAssistente, currentLab?.id, conversa, servicoEmFoco]);
  // O workflow aberto agora veio de um botão do assistente? Então, ao salvar,
  // a volta é para a conversa, onde está o próximo passo.
  const abertoPeloAssistente = useRef(false);
  useEffect(() => {
    let vivo = true;
    // A conversa deste laboratório volta como estava (recarregar a página não
    // é começar outra); a de outro laboratório não vem junto.
    const guardada = lerConversaGuardada(currentLab?.id);
    setConversa(guardada.conversa);
    setServicoEmFoco(guardada.foco);
    setSugestoesNaoVistas(0);
    assistenteLigado(currentLab?.id).then(v => {
      if (!vivo) return;
      setComAssistente(v);
      // Com o assistente ligado, ele é a página de abertura. Só troca se a
      // pessoa ainda está na aba inicial: quem já navegou fica onde está.
      if (v) {
        setVisitadas(prev => new Set(prev).add('assistente'));
        setActiveTab(atual => (atual === 'dashboard' ? 'assistente' : atual));
      } else {
        setActiveTab(atual => (atual === 'assistente' ? 'dashboard' : atual));
      }
    });
    return () => { vivo = false; };
  }, [currentLab?.id]);

  // Função corrigida
  // Saída para quando a conta não está vinculada a nenhum laboratório.
  // Sem isto a tela de 'no_access' é um beco sem saída: não há outro caminho
  // no app para criar o primeiro laboratório.
  const createFirstLab = async () => {
    const nome = firstLabName.trim();
    if (!nome) return;

    setLoading(true);
    setFirstLabError('');
    try {
      const lab = await createLaboratory({
        name: nome, owner_id: user.id,
        description: `Criado por ${usuarioDeEmail(user.email)}`,
      });

      await addLabMember({
        laboratory_id: lab.id, user_id: user.id, role: 'admin', status: 'active',
      });

      await setActiveLaboratory(user.id, lab.id);

      setFirstLabName('');
      setNeedsAccessCheck(true);
      setSystemState('loading');
      await checkUserAccess();
    } catch (error) {
      console.error('Erro ao criar laboratório:', error);
      setFirstLabError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Entra em cena com a lista pronta, venha ela por associação ou por ser
  // super admin. Mantém a escolha do lab padrão num lugar só — antes de ter
  // dois caminhos de carga isso não fazia diferença; agora faz.
  const aplicarLabs = (labs, profileData) => {
    setUserLabs(labs);

    let defaultLab = null;
    if (profileData?.active_laboratory_id) {
      defaultLab = labs.find(l => l.id === profileData.active_laboratory_id);
    }
    if (!defaultLab && labs.length > 0) {
      defaultLab = labs[0];
    }

    if (defaultLab) {
      setCurrentLab(defaultLab);
      setUserRole(defaultLab.user_role);
      console.log('✅ Lab padrão definido:', defaultLab.name, 'Role:', defaultLab.user_role);
    }

    setSystemState(labs.length > 0 ? 'active' : 'no_access');
    setNeedsAccessCheck(false);
  };

  const checkUserAccess = async () => {
    if (!needsAccessCheck && systemState !== 'loading') {
      return;
    }
    console.log('🔐 Verificando acesso do usuário:', usuarioDeEmail(user.email));
    setSystemState('loading');
    setNeedsAccessCheck(false);

    try {
      // Carregar perfil
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);

      // Super admin não passa por lab_members: ele não é membro de nenhum
      // laboratório, e por associação a lista viria vazia. O papel é 'admin'
      // em todos porque é isso que a RLS de fato concede a ele.
      if (profileData?.is_super_admin) {
        const todos = await listAllLaboratories();
        console.log('🛡️ Super admin — laboratórios visíveis:', todos?.length);
        aplicarLabs(
          (todos || []).map(lab => ({ ...lab, user_role: 'admin', member_id: null })),
          profileData,
        );
        return;
      }

      // PRIMEIRO: Buscar apenas os lab_members
      const labMembers = await fetchActiveMemberships(user.id);

      console.log('Lab Members:', labMembers);

      // SEGUNDO: Buscar os laboratórios separadamente
      if (labMembers && labMembers.length > 0) {
        const labIds = labMembers.map(m => m.laboratory_id);

        const laboratories = await fetchActiveLaboratories(labIds);

        console.log('Laboratories:', laboratories);

        // TERCEIRO: Combinar os dados
        const userLabs = labMembers.map(member => {
          const lab = laboratories.find(l => l.id === member.laboratory_id);
          return {
            ...lab,
            user_role: member.role,
            member_id: member.id
          };
        }).filter(lab => lab.id); // Remove any null labs

        console.log('User Labs combinados:', userLabs);

        aplicarLabs(userLabs, profileData);
        return;
      }

      setSystemState('no_access');
      setNeedsAccessCheck(false);

    } catch (error) {
      console.error('❌ Erro ao verificar acesso:', error);
      setSystemState('no_access');
      setNeedsAccessCheck(false);
    }
  };

  const loadLabData = async () => {
    try {
      await Promise.all([loadClients(), loadServices(), loadParcelas()]);
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
    }
  };

  /**
   * Parcelas em aberto e pagas, agrupadas por serviço.
   *
   * Tolerante: o Financeiro ainda mostra valor e situação pelos campos
   * espelhados no serviço, então falhar aqui degrada a tela em vez de derrubá-la.
   * `total` vai junto em cada parcela porque o botão precisa saber se é "1 de 3"
   * ou a única — e ele não enxerga o grupo.
   */
  const loadParcelas = async () => {
    if (!currentLab) return;

    try {
      const todas = (await listInvoices(currentLab.id) || []).filter(f => !f.cancelada_em);

      // Entrada paga por serviço, das faturas do tipo 'repasse'.
      const entradas = {};
      // E a entrada EMITIDA, que é outra pergunta. Paga responde "quanto
      // abater no fim"; emitida responde "ainda cabe cobrar?". Enquanto só a
      // paga existia, uma entrada emitida e não paga deixava o botão de
      // cobrar de pé — e cobrava o mesmo embrião duas vezes.
      const emitidas = {};
      // `!cancelada_em` em toda leitura de cobrança: a entrada superada por
      // uma fatura de serviço é cancelada, e contá-la aqui faria o cartão
      // anunciar uma cobrança que não existe mais e o botão de receber
      // aparecer sobre um documento morto.
      const viva = (f) => !f.cancelada_em;

      todas.filter(f => f.tipo === 'repasse' && f.service_id && viva(f)).forEach(f => {
        entradas[f.service_id] = (entradas[f.service_id] || 0) + Number(f.valor_pago || 0);
        const ja = emitidas[f.service_id];
        emitidas[f.service_id] = {
          valor:  (ja?.valor || 0) + Number(f.valor_total || 0),
          numero: ja?.numero || f.numero_exibicao,
          em:     ja?.em     || f.emitida_em,
        };
      });
      setEntradaPagaPorServico(entradas);
      setEntradaEmitidaPorServico(emitidas);

      setServicoDaFatura(Object.fromEntries(
        todas.filter(f => f.service_id).map(f => [f.id, f.service_id])));

      // A forma combinada, por serviço: sem isto ela é digitada na hora de
      // faturar e nunca mais aparece em lugar nenhum. Só da fatura de serviço,
      // que é onde a combinação de pagamento é fechada.
      const formas = {};
      todas.filter(f => f.service_id && f.tipo === 'servico' && viva(f)).forEach(f => {
        if (f.forma_pagamento) {
          formas[f.service_id] = {
            forma: f.forma_pagamento, obs: f.forma_pagamento_obs,
          };
        }
      });
      setFormaPorServico(formas);

      /**
       * AS PARCELAS DA ENTRADA ENTRAM AQUI TAMBÉM.
       *
       * Este mapa era montado só com faturas de serviço, e ele é a única fonte
       * da lista de parcelas da tela. A consequência era que a parcela da
       * entrada não aparecia em lugar nenhum, e o único botão que abre o
       * lançamento de recebimento percorre justamente esta lista: não havia
       * caminho no app para registrar que a entrada foi paga.
       *
       * E o abatimento da entrada no faturamento final depende do que foi
       * PAGO. Entrada que não pode ser paga nunca abate nada — toda a regra de
       * encontro de contas, com teste e tudo, era código morto em produção. O
       * cliente recebia a cobrança da entrada e depois o serviço cheio.
       *
       * `ehEntrada` viaja na parcela porque a tela precisa dizer de qual das
       * duas cobranças ela é, e porque quitar o serviço inteiro continua sendo
       * só das parcelas de serviço.
       */
      const deServico = todas.filter(f => f.service_id && f.tipo === 'servico' && viva(f));
      const deEntrada = todas.filter(f => f.service_id && f.tipo === 'repasse' && viva(f));
      const cobrancas = [...deServico, ...deEntrada];

      /**
       * A CONTA DE CADA SERVIÇO: cobrado, recebido, falta, quando vence.
       *
       * É a soma das faturas VIVAS daquele serviço, e isso já implementa a
       * regra da supersessão sem nenhum caso especial: antes de faturar existe
       * só a entrada; ao faturar, a entrada é cancelada e sobra só a fatura do
       * serviço, já com o recebido da entrada movido para ela.
       *
       * Existe porque o Financeiro só sabia dizer o VALOR de um serviço
       * faturado, e não quanto dele já entrou. Numa aba de dinheiro, "faturei
       * R$ 6.600" sem "recebi R$ 0" é meia resposta.
       */
      const caixa = {};
      cobrancas.forEach(f => {
        const c = caixa[f.service_id] || { pagoServico: 0, vence: null };
        // A entrada paga tem mapa próprio (`entradaPagaPorServico`), porque ela
        // responde outra pergunta — quanto abater no acerto final.
        if (f.tipo !== 'repasse') {
          c.pagoServico += Math.round((Number(f.valor_pago) || 0) * 100);
        }
        // O mais próximo entre as cobranças vivas do serviço.
        if (f.proximo_vencimento && (!c.vence || f.proximo_vencimento < c.vence)) {
          c.vence = f.proximo_vencimento;
        }
        caixa[f.service_id] = c;
      });
      setCaixaPorServico(Object.fromEntries(Object.entries(caixa).map(([id, c]) => [id, {
        pagoServico: c.pagoServico / 100, vence: c.vence,
      }])));

      if (cobrancas.length === 0) {
        setParcelasPorServico({}); setCaixaPorServico({}); setVersaoDasParcelas(v => v + 1); return;
      }

      const parcelas = await listInstallments(cobrancas.map(f => f.id)) || [];
      const faturaPorId = Object.fromEntries(cobrancas.map(f => [f.id, f]));

      const porServico = {};
      parcelas.forEach(p => {
        const fatura = faturaPorId[p.invoice_id];
        if (!fatura) return;
        (porServico[fatura.service_id] = porServico[fatura.service_id] || []).push({
          ...p,
          ehEntrada: fatura.tipo === 'repasse',
          numeroDaFatura: fatura.numero_exibicao,
          // A data em que a cobrança SAIU. Sem ela, reimprimir o documento da
          // entrada o carimbaria com a data de hoje — e o papel na mão do
          // cliente passaria a discordar do que o sistema diz.
          emitidaEm: fatura.emitida_em,
        });
      });

      // `total` é por FATURA, não por serviço: "parcela 1 de 2" tem de contar
      // as parcelas do documento em que ela está, e um serviço pode ter a
      // fatura da entrada e a do serviço ao mesmo tempo.
      Object.values(porServico).forEach(lista => {
        lista.sort((a, b) => (a.ehEntrada === b.ehEntrada)
          ? a.numero - b.numero
          : (a.ehEntrada ? -1 : 1));
        const porFatura = {};
        lista.forEach(p => { porFatura[p.invoice_id] = (porFatura[p.invoice_id] || 0) + 1; });
        lista.forEach(p => { p.total = porFatura[p.invoice_id]; });
      });

      setParcelasPorServico(porServico);
      // De qual serviço é cada parcela. A trilha de auditoria precisa dele para
      // levar de uma baixa de pagamento ao financeiro do serviço: a linha da
      // trilha só tem o id da parcela, e a parcela só aponta para a fatura.
      setServicoDaParcela(Object.fromEntries(
        Object.entries(porServico).flatMap(([servicoId, lista]) =>
          (lista || []).map(parcela => [parcela.id, servicoId]))));
      setVersaoDasParcelas(v => v + 1);
    } catch (error) {
      console.error('Erro ao carregar parcelas:', error);
      setParcelasPorServico({});
      setCaixaPorServico({});
      setEntradaPagaPorServico({});
      // `null` é "não sei", diferente de `{}` = "nenhuma emitida". Falha de
      // leitura não pode virar convite para emitir: seria oferecer a segunda
      // cobrança do mesmo embrião justamente quando o app não enxerga a
      // primeira.
      setEntradaEmitidaPorServico(null);
    }
  };

  const loadClients = async () => {
    if (!currentLab) return;
    
    try {
      setClients(await listClients(currentLab.id) || []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const loadServices = async () => {
    if (!currentLab) return;
    
    try {
      setServices(await listServices(currentLab.id) || []);
    } catch (error) {
      console.error('Erro ao carregar serviços:', error);
    }
  };

  // Licença deste computador. Fica aqui em cima, junto dos outros hooks, porque
  // React exige que a ordem seja a mesma em toda renderização — chamar depois
  // de um `return` condicional quebraria isso.
  const licenca = useLicenca(currentLab?.id);

  const handleLogout = async () => {
    const userId = (await getSession())?.user?.id || null;
    const naFila = await countPending(userId);

    if (naFila > 0) {
      const ok = window.confirm(
        [
          `Você tem ${naFila} lançamento(s) que ainda não subiram para o servidor.`,
          'Eles só podem ser enviados com a sua sessão. Se sair agora, ficam guardados no aparelho e sobem quando você entrar de novo com internet.',
          'Sair mesmo assim?',
        ].join('\n\n')
      );
      if (!ok) return;
    }

    await signOut();
  };

  // Função para trocar de laboratório
  const switchLaboratory = async (lab) => {
    setCurrentLab(lab);
    setUserRole(lab.user_role); // user_role já vem do mapeamento correto
    setShowLabDropdown(false);

    try {
      await setActiveLaboratory(user.id, lab.id);

      setProfile(prev => ({ ...prev, active_laboratory_id: lab.id }));
    } catch (error) {
      console.error('Erro ao atualizar laboratório ativo:', error);
    }
  };

  // O cliente abre com os locais prontos para editar: os gravados, ou o do
  // cadastro antigo lido das colunas soltas, ou um em branco — o de cobrança.
  const clienteParaEditar = (item) => {
    const locais = locaisDoCliente(item);
    return { ...(item || {}), locais: locais.length ? locais : [novoLocal({ cobranca: true })] };
  };

  const openModal = (type, item = null, inicial = {}) => {
    setModalType(type);
    setSelectedItem(item);
    // Serviço novo nasce datado de hoje e com os preços padrão do laboratório.
    // `inicial` é o que já se sabe dele — o dia clicado no calendário.
    setFormData(type === 'client' ? clienteParaEditar(item) : (item || (type === 'service'
      ? { ...novoCadastro({ ...precosParaServicoNovo(currentLab), ...entradaParaServicoNovo(currentLab) }), ...inicial }
      : {})));
    setShowModal(true);
    setServicoEmDestaque(null);
  };

  /**
   * Volta ao serviço que estava sendo lançado, com a fazenda nova escolhida
   * quando houver uma. É o caminho de volta de "+ Nova fazenda".
   */
  const retomarServico = (clientId = null) => {
    const espera = servicoEmEspera;
    setServicoEmEspera(null);
    setModalType('service');
    setSelectedItem(espera.selectedItem);
    setFormData(clientId ? { ...espera.formData, client_id: clientId } : espera.formData);
  };

  const closeModal = () => {
    // Cancelar o cadastro da fazenda não cancela o serviço: volta para ele.
    if (servicoEmEspera) {
      retomarServico();
      return;
    }
    setShowModal(false);
    setModalType('');
    setSelectedItem(null);
    setFormData({});
  };

  // Cadastrar a fazenda sem perder o serviço meio preenchido.
  const abrirNovaFazendaDoServico = () => {
    setServicoEmEspera({ formData, selectedItem });
    setModalType('client');
    setSelectedItem(null);
    setFormData(clienteParaEditar(null));
  };

  const canEdit = () => {
    return ['admin', 'moderator'].includes(userRole);
  };

  const canManageServices = () => {
    return ['admin', 'moderator', 'technician'].includes(userRole);
  };

  const handleSave = async () => {
    if (!canEdit() && modalType !== 'service') {
      alert('Você não tem permissão para esta ação');
      return;
    }

    setLoading(true);
    try {
      if (modalType === 'client') {
        let clientId = selectedItem?.id;
        const cliente = prepararCliente(formData);
        if (selectedItem) {
          await updateClient(selectedItem.id, cliente, currentLab.id, selectedItem.updated_at);
        } else {
          clientId = newClientId();
          await insertClient({
            id: clientId,
            ...cliente,
            user_id: user.id,
            laboratory_id: currentLab.id,
          });
        }
        await loadClients();

        // A fazenda foi cadastrada de dentro de um serviço: volta para ele,
        // já com ela escolhida, em vez de fechar tudo.
        if (servicoEmEspera) {
          retomarServico(clientId);
          return;
        }
      } else if (modalType === 'service') {
        if (!canManageServices()) {
          alert('Você não tem permissão para gerenciar serviços');
          return;
        }

        // O botão já não deixa chegar aqui com falta; esta é a rede para o
        // caso de chegar por outro caminho.
        const faltas = validarCadastro(formData);
        if (faltas.length > 0) {
          alert(`Falta informar ${faltas.join(', ')}.`);
          return;
        }

        // Só o que é do cadastro desce — e não o `formData` inteiro, que em
        // edição carrega a linha toda (jsonb do workflow, carimbos, número).
        const clientName = clients.find(c => c.id === formData.client_id)?.name || '';
        const cadastro = montarCadastro(formData, { clientName });

        if (selectedItem) {
          await updateService(selectedItem.id, cadastro, currentLab.id, selectedItem.updated_at);
        } else {
          const id = newServiceId();
          await insertService({
            id,
            ...cadastro,
            user_id: user.id,
            laboratory_id: currentLab.id,
          });
          setServicoEmDestaque(id);
        }
        loadServices();
      }
      closeModal();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * EXCLUIR SERVIÇO PASSA PELO MODAL, e não pelo balão do navegador.
   *
   * O `window.confirm` de antes tinha dois defeitos, e o segundo é o grave: não
   * dizia O QUE ia embora, e confirmava no ENTER. Quem digitava na tela e apertou
   * Enter por reflexo apagava um serviço com meses de lançamento — e a exclusão
   * cascateia para sessões, embriões e faturas.
   *
   * Aqui só abre o modal. Quem apaga é `confirmarExclusaoDeServico`, depois de a
   * pessoa digitar o código.
   */
  const excluirServico = (service) => {
    if (!canEdit()) {
      alert('Você não tem permissão para esta ação');
      return;
    }
    setServicoParaExcluir(service);
  };

  /**
   * O que o modal de exclusão precisa dizer, calculado uma vez.
   *
   * Montado aqui e não dentro do JSX porque as duas regras — o que vai junto e o
   * peso — leem as mesmas parcelas, e chamá-las dentro do atributo as repetiria a
   * cada render do modal aberto.
   */
  const exclusao = useMemo(() => {
    if (!servicoParaExcluir) return { descricao: '', aviso: null, levaTambem: [] };
    const parcelas = parcelasPorServico[servicoParaExcluir.id] || [];
    const rotulo = servicoParaExcluir.service_label
      ? ` · ${servicoParaExcluir.service_label}` : '';
    return {
      descricao: `${servicoParaExcluir.client_name || 'Sem cliente'}${rotulo}`,
      aviso: pesoDaExclusao(servicoParaExcluir, { parcelas }).motivo,
      levaTambem: oQueVaiComOServico(servicoParaExcluir, { services, parcelas }),
    };
  }, [servicoParaExcluir, parcelasPorServico, services]);

  const confirmarExclusaoDeServico = async () => {
    const service = servicoParaExcluir;
    if (!service) return;

    setLoading(true);
    try {
      await deleteService(service.id, currentLab.id);
      setServicoParaExcluir(null);
      // O serviço apagado pode estar aberto em outra tela: o modal de dinheiro
      // dele ficaria de pé sobre um registro que não existe mais.
      if (servicoNoFinanceiro?.id === service.id) setServicoNoFinanceiro(null);
      if (selectedService?.id === service.id) closeWorkflowModal();
      loadServices();
      // As parcelas vão junto na cascata; sem recarregar, a aba de dinheiro
      // continuaria somando a fatura de um serviço que não existe.
      loadParcelas();
    } catch (error) {
      console.error('Erro ao excluir serviço:', error);
      alert(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!canEdit()) {
      alert('Você não tem permissão para esta ação');
      return;
    }

    if (!window.confirm('Tem certeza que deseja excluir?')) return;
    
    setLoading(true);
    try {
      if (type === 'client') await deleteClient(id, currentLab.id);
      else await deleteService(id, currentLab.id);
      
      if (type === 'client') {
        loadClients();
      } else {
        loadServices();
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  // Funções de filtro de data (do código anterior)
  // ===== FUNÇÕES AUXILIARES AGORA SÃO IMPORTADAS DOS UTILS =====
  // calculateOperationalCosts, getOperationalCostsDetails, hasOperationalCosts
  // calculateServiceValue, checkAllContractedStagesDone, isReadyForBilling
  // getServiceStatus - Todas importadas de '../utils'

  // Função para faturar um serviço
  /**
   * Cobrar a ENTRADA do serviço, antes do faturamento final.
   *
   * Fatura à parte (tipo 'repasse'), que pode existir ou não: sem ela, a
   * entrada simplesmente não é cobrada e nada é abatido no fim.
   */
  const handleChargeEntrada = (service) => {
    if (!canManageServices()) {
      alert('Você não tem permissão para faturar serviços');
      return;
    }

    /**
     * A trava é a MESMA de quem oferece, e mora aqui.
     *
     * Esta função checava só se o valor era maior que zero. Todas as outras
     * condições — TE concluída, entrada já emitida, serviço já faturado,
     * modelo em que a entrada não abate — viviam em quem chamava, e os dois
     * chamadores filtravam de jeitos diferentes. Trava que depende de quem
     * chama é trava que o terceiro chamador não tem.
     */
    const jaEmitida = entradaEmitidaPorServico === null
      ? null
      : Boolean(entradaEmitidaPorServico[service.id]);

    if (jaEmitida === null) {
      alert('Não foi possível conferir se este serviço já tem fatura de entrada. '
        + 'Recarregue a página antes de cobrar, para não cobrar o mesmo embrião duas vezes.');
      return;
    }

    const { deve, motivo, valor, quantidade } = entradaAEmitir(service, currentLab, jaEmitida);
    if (!deve) {
      alert(MOTIVO_DA_ENTRADA[motivo] || 'Este serviço não deve ter fatura de entrada agora.');
      return;
    }
    if (valor <= 0) {
      alert('Este serviço ainda não tem embriões transferidos para calcular a entrada.');
      return;
    }

    setModoCobranca('entrada');
    setSelectedBillingService({ ...service, entrada_embrioes: quantidade });
    setShowBillingModal(true);
  };

  const handleBillService = async (service) => {
    if (!canManageServices()) {
      alert('Você não tem permissão para faturar serviços');
      return;
    }

    setModoCobranca('servico');
    const calculatedValue = calculateServiceValue(service);

    if (calculatedValue <= 0) {
      alert('Este serviço não possui valor para faturamento. Complete as etapas necessárias primeiro.');
      return;
    }

    if (service.billed) {
      alert('Este serviço já foi faturado.');
      return;
    }

    setSelectedBillingService(service);
    setShowBillingModal(true);
  };

  /**
   * Faturar passou a emitir uma FATURA com parcelas.
   *
   * `billed`/`billed_value`/`paid` no serviço continuam existindo, mas agora
   * são reflexo: um gatilho no banco os recalcula a partir das parcelas. Por
   * isso aqui não se escreve neles — escrever nos dois lugares é como eles
   * passariam a discordar.
   *
   * `billing_notes` e `billed_date` seguem no serviço porque são do
   * faturamento em si, não do recebimento, e as telas atuais os leem.
   */
  const handleBillingConfirm = async (billingData) => {
    if (!selectedBillingService) return;

    const parcelas = billingData.parcelasCalculadas || [];
    if (parcelas.length === 0) {
      alert('Não há valor a faturar neste serviço.');
      return;
    }

    setLoading(true);
    try {
      const ehEntrada = billingData.modo === 'entrada';

      const faturaNova = await criarFatura({
        laboratoryId: currentLab.id,
        clientId: selectedBillingService.client_id,
        serviceId: selectedBillingService.id,
        tipo: ehEntrada ? 'repasse' : 'servico',
        emitidaEm: billingData.billingDate,
        observacoes: billingData.notes || null,
        formaPagamento:    billingData.formaPagamento,
        formaPagamentoObs: billingData.formaPagamentoObs,
        parcelas,
      });

      /**
       * A fatura do serviço SUPERA a da entrada.
       *
       * A entrada cobrou, antes da hora, parte deste mesmo serviço. O acerto
       * final sai pelo preço cheio, então a cobrança provisória não pode
       * continuar de pé: os mesmos embriões ficariam em dois documentos.
       *
       * O dinheiro que a entrada recebeu passa para a fatura nova, e é assim
       * que "R$ 1.500 de fatura com R$ 950 de entrada paga" vira saldo de
       * R$ 550 em TODO relatório, sem nenhum deles precisar conhecer a regra.
       */
      let superada = null;
      if (!ehEntrada) {
        superada = await entradaSuperadaPelaFatura(selectedBillingService.id, faturaNova?.id);
      }

      // Só a cobrança do serviço carimba o faturamento. A entrada é cobrança à
      // parte: marcar o serviço como faturado por causa dela o tiraria da fila
      // de "a faturar" sem que o serviço tivesse sido cobrado.
      if (!ehEntrada) {
        /**
         * SEM carimbo de concorrência, e isto é deliberado.
         *
         * `criarFatura` acabou de inserir a fatura, e o gatilho
         * `parcelas_espelham_no_servico` respondeu atualizando o serviço
         * (billed, billed_value) — o que moveu `services.updated_at`.
         *
         * Passar o carimbo lido ANTES do faturamento exigia que o serviço não
         * tivesse mudado, quando quem o mudou foi a própria operação em curso.
         * Nenhuma linha casava e o app estourava com "registro alterado por
         * outra pessoa" DEPOIS de a fatura já existir — a pessoa via o erro,
         * tentava de novo, e emitia a segunda.
         *
         * O carimbo existe para barrar a edição de OUTRA pessoa. Aqui não há
         * outra pessoa: é a continuação da mesma gravação.
         */
        await updateServiceBilling(selectedBillingService.id, {
          billed_date: new Date(billingData.billingDate).toISOString(),
          billing_notes: billingData.notes,
        }, currentLab.id);
      }

      await Promise.all([loadServices(), loadParcelas()]);
      setShowBillingModal(false);
      const oque = billingData.modo === 'entrada' ? 'Entrada cobrada' : 'Serviço faturado';
      const emQuantas = parcelas.length > 1 ? ` em ${parcelas.length}x` : '';

      // A supersessão é dita em voz alta: o cliente tinha uma cobrança de
      // entrada em aberto e ela deixou de existir. Quem faturou precisa saber
      // disso para não procurar a fatura sumida depois.
      const ressalva = superada
        ? `

A fatura de entrada ${superada.canceladas.join(', ')} foi cancelada: `
          + 'esta cobra o serviço inteiro.'
          + (superada.movidos > 0
            ? ` O que já havia sido recebido nela passou para esta fatura e abate o saldo.`
            : '')
        : '';

      alert(`${oque}${emQuantas}!${ressalva}`);
    } catch (error) {
      console.error('Erro ao faturar serviço:', error);
      alert('Erro ao faturar serviço: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Pagamento agora é por PARCELA.
   *
   * `service.paid` virou reflexo mantido por gatilho a partir das parcelas —
   * escrever nele aqui seria desfeito no próximo recálculo, e por um tempo os
   * dois mostrariam coisas diferentes.
   */
  /**
   * Refaturar a ENTRADA: apaga a fatura da entrada e a devolve para "a
   * emitir", recontada pelos transferidos de agora. Mesma trava do serviço:
   * entrada com recebimento lançado não se apaga (a conferência mora em
   * `refaturarEntrada`, que lê os recebimentos e não só a quitação).
   */
  const handleRefaturarEntrada = async (service) => {
    if (!canManageServices()) {
      alert('Você não tem permissão para refaturar');
      return;
    }
    const aviso = [
      `Refaturar a entrada do serviço #${service.numero || service.service_code || ''}?`,
      '',
      'A fatura da entrada será APAGADA e a entrada volta para "a emitir",',
      `contando os ${Number(service.te_quantity) || 0} embrião(ões) transferidos hoje na TE.`,
      'Depois é só emitir de novo; ela sai com número novo.',
      '',
      'Quem apagou e quando fica registrado na trilha de auditoria.',
    ].join('\n');
    if (!window.confirm(aviso)) return;

    setLoading(true);
    try {
      const apagadas = await refaturarEntrada(service);
      await Promise.all([loadServices(), loadParcelas()]);
      alert(apagadas.length > 0
        ? `Entrada ${apagadas.join(', ')} apagada. Ela voltou para "a emitir".`
        : 'A entrada voltou para "a emitir".');
    } catch (error) {
      console.error('Erro ao refaturar a entrada:', error);
      alert(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refaturar: cancela o documento emitido e devolve o serviço para "A Faturar".
   *
   * Não edita a fatura no lugar, de propósito. Reescrever manteria o mesmo
   * número de documento com outro conteúdo; cancelando, a antiga fica visível
   * como cancelada e a nova nasce com número próprio — é o que permite
   * explicar ao cliente por que recebeu duas.
   *
   * Parcela paga BLOQUEIA. Cancelar um documento contra o qual já entrou
   * dinheiro deixa o caixa apontando para uma fatura que não existe mais, e
   * "desfazer o pagamento" é uma decisão de quem cuida do financeiro, não um
   * efeito colateral de corrigir um valor.
   */
  const handleRefaturar = async (service) => {
    if (!canManageServices()) {
      alert('Você não tem permissão para refaturar');
      return;
    }

    // Refaturar mexe só na fatura do SERVIÇO. A da entrada tem vida própria e
    // não sai junto, então a parcela dela não entra nesta conferência.
    const parcelas = (parcelasPorServico[service.id] || []).filter(p => !p.ehEntrada);
    const comRecebimento = parcelas.filter(p => p.paga_em);
    if (comRecebimento.length > 0) {
      alert(
        `Este serviço tem ${comRecebimento.length} parcela(s) já paga(s).\n\n`
        + 'Desfaça o pagamento antes de refaturar — senão o dinheiro recebido '
        + 'fica apontando para uma fatura cancelada.'
      );
      return;
    }

    const aviso = [
      `Refaturar o serviço #${service.numero || service.service_code || ''}?`,
      '',
      'A fatura atual será APAGADA e o serviço volta para "A Faturar".',
      'Lá você ajusta os valores e emite uma nova, com número novo.',
      '',
      'Quem apagou e quando fica registrado na trilha de auditoria.',
    ].join('\n');
    if (!window.confirm(aviso)) return;

    setLoading(true);
    try {
      const apagadas = await refaturar(service.id);
      await Promise.all([loadServices(), loadParcelas()]);
      alert(apagadas.length > 0
        ? `Fatura ${apagadas.join(', ')} apagada. O serviço voltou para "A Faturar".`
        : 'O serviço voltou para "A Faturar".');
    } catch (error) {
      console.error('Erro ao refaturar:', error);
      alert(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Abre os lancamentos da parcela.
   *
   * Antes isto marcava pago com um clique, e `paga_em` era escrito pela tela.
   * Agora `paga_em` e CONSEQUENCIA: um gatilho o escreve quando a soma dos
   * recebimentos alcanca o valor devido. A tela lanca o que entrou — valor,
   * meio, data e conta —, e a quitacao se resolve sozinha.
   *
   * O que isso destrava: recebimento parcial, recebimento em duas datas, meio
   * diferente do combinado no faturamento, e antecipacao (onde o cliente paga
   * o valor cheio e entra menos na conta).
   */
  /**
   * O check de "pago" do serviço inteiro.
   *
   * ATALHO, e nao segunda verdade. Ele nao escreve `services.paid` a mao — isso
   * criaria um campo dizendo "pago" enquanto o saldo do cliente diz que falta
   * receber, sem como saber qual dos dois vale.
   *
   * Ele LANCA: cria um recebimento do que falta em cada parcela aberta. A
   * quitacao continua vindo da soma, o saldo continua batendo, e quem quiser o
   * detalhe (meio, conta, antecipacao) abre a parcela e edita.
   *
   * E o caminho para o pagamento que aconteceu fora do sistema — dinheiro,
   * permuta, acerto de boca — onde abrir quatro lancamentos e atrito sem ganho.
   */
  const handleTogglePago = async (service) => {
    if (!canEdit()) {
      alert('Você não tem permissão para lançar recebimentos');
      return;
    }
    if (!service.billed) {
      alert('Fature o serviço antes de marcar como pago.');
      return;
    }

    setLoading(true);
    try {
      if (service.paid) {
        const aviso = [
          `Desmarcar o pagamento do serviço #${service.numero || service.service_code || ''}?`,
          '',
          'Os recebimentos lançados nas parcelas serão APAGADOS, e o saldo do',
          'cliente volta a subir.',
          '',
          'Se você só quer corrigir um valor ou o meio, use o botão da parcela',
          'em vez deste — lá dá para editar sem apagar tudo.',
        ].join('\n');
        if (!window.confirm(aviso)) return;
        await desfazerQuitacaoDoServico(service.id);
      } else {
        await quitarServico(currentLab.id, service.id);
      }
      await Promise.all([loadServices(), loadParcelas()]);
    } catch (error) {
      console.error('Erro ao marcar pagamento:', error);
      alert(describeError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = (service, parcela) => {
    if (!canEdit()) {
      alert('Você não tem permissão para lançar recebimentos');
      return;
    }
    if (!parcela) {
      alert('Este serviço ainda não tem fatura emitida.');
      return;
    }
    setRecebendo({ service, parcela });
  };

  // Função para obter ícones de status financeiro
  // Faturamento e pagamento viram marca em vez de icone colorido: verde x
  // laranja nao sobrevive a impressao nem ao daltonismo, e laranja nem estava
  // na paleta. `mark` vazio e proposital — servico em andamento nao tem nada a
  // sinalizar, e marca que aparece sempre nao informa nada.
  const getFinancialStatusIcons = (service) => {
    const calculatedValue = calculateServiceValue(service);
    const hasValue = calculatedValue > 0;
    const isFinished = getServiceStatus(service) === 'Finalizado';

    return {
      billing: {
        mark: service.billed
          ? MARK.OK
          : (isFinished && hasValue ? MARK.PENDING : ''),
        title: service.billed
          ? `Faturado: R$ ${(service.billed_value || 0).toLocaleString()} em ${service.billed_date ? new Date(service.billed_date).toLocaleDateString('pt-BR') : 'N/A'}`
          : (!isFinished
              ? 'Serviço em andamento - aguardando conclusão'
              : (hasValue
                  ? `Pendente faturamento: R$ ${calculatedValue.toLocaleString()}`
                  : 'Sem valor para faturamento'
                )
            )
      },
      payment: {
        mark: service.paid ? MARK.OK : (service.billed ? MARK.PENDING : ''),
        title: service.paid
          ? `Pago: R$ ${(service.billed_value || 0).toLocaleString()} em ${service.paid_date ? new Date(service.paid_date).toLocaleDateString('pt-BR') : 'N/A'}`
          : service.billed
            ? `Pendente pagamento: R$ ${(service.billed_value || 0).toLocaleString()}`
            : 'Aguardando faturamento'
      }
    };
  };

  const getStageCards = (service) => {
    const cards = [];

    if (service.opu_done && service.opu_quantity > 0) {
      cards.push({
        label: 'OPU',
        value: service.opu_quantity,
        unit: 'óvulos'
      });
    }

    if (service.lab_done && service.lab_quantity > 0) {
      cards.push({
        label: 'LAB',
        value: service.lab_quantity,
        unit: 'embriões'
      });
    }

    if (service.te_done && service.te_quantity > 0) {
      cards.push({
        label: 'TE',
        value: service.te_quantity,
        unit: 'transferidos'
      });
    }

    if (service.pregnancy_60d_done && service.pregnancy_60d_quantity > 0) {
      cards.push({
        label: 'PRENHEZ',
        value: service.pregnancy_60d_quantity,
        unit: 'confirmadas'
      });
    }

    return cards;
  };

  /**
   * PDF da cobrança.
   *
   * `billingInfo.parcelas` é opcional: quando vem, o documento passa a
   * declarar o parcelamento. Sem isso o cliente recebia um papel com um total
   * só, e o "4x" existia apenas dentro do app — o combinado não estava em
   * lugar nenhum que ele pudesse conferir.
   */
  const generateBillingPDF = async (service, lab, clientsList, billingInfo) => {
    if (!service) return;

    // A janela abre ANTES de buscar os transferidos: aberta depois de um
    // `await`, o navegador já não a liga ao clique e bloqueia como pop-up.
    // Pop-up bloqueado devolve `null`, e o `.document` estourava com "Cannot
    // read properties of null" — mensagem que manda a pessoa procurar bug
    // onde só falta uma permissão do navegador.
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('O navegador bloqueou a janela do PDF. Permita pop-ups para este site e tente de novo.');
      return;
    }
    printWindow.document.write('<p style="font-family:Arial;padding:40px">Montando a fatura…</p>');

    try {
      // Sem a lista o papel ainda serve; falhar aqui não pode impedir a fatura.
      const transferidos = await transferenciasComDiagnostico(service.id)
        .then(({ transferencias, checagens }) => linhasDosTransferidos(transferencias, checagens))
        .catch((err) => { console.warn('[pdf] transferidos indisponíveis:', err.message); return []; });

      const client = clientsList.find(c => c.id === service.client_id);
      const billingDate = new Date(billingInfo.billingDate);

      /**
       * O TOTAL DO PAPEL É A SOMA DAS PARCELAS. Sempre.
       *
       * Era recalculado aqui, e o papel não fechava consigo mesmo. O PDF é
       * gerado ANTES da confirmação, quando `service.billed` ainda é falso, e
       * então o total saía do valor BRUTO enquanto as parcelas listadas logo
       * abaixo somavam o líquido. Com entrada abatida, o cliente recebia um
       * documento que dizia um total e listava parcelas de outro.
       *
       * E a fatura da ENTRADA usava este mesmo gerador sem saber disso:
       * imprimia o valor do serviço inteiro como total, com as parcelas da
       * entrada embaixo.
       *
       * As parcelas são o que foi realmente gravado. Tirar o total delas
       * elimina as duas divergências de uma vez, e o documento passa a fechar
       * por construção.
       */
      const parcelasDoPDF = billingInfo.parcelas || [];
      const somaDasParcelas = parcelasDoPDF.length > 0
        ? Math.round(parcelasDoPDF.reduce((s, p) => s + (Number(p.valor) || 0), 0) * 100) / 100
        : null;

      const ehFaturaDeEntrada = billingInfo.modo === 'entrada';
      const abatidoNoPDF = Number(billingInfo.entradaAbatida) || 0;

      const totalValue = somaDasParcelas !== null
        ? somaDasParcelas
        : (service.billed ? parseFloat(service.billed_value) || 0 : calculateServiceValue(service));

      // Na entrada não há detalhamento de modelo nem de custo: ela cobra
      // embrião transferido × taxa, e é só isso que o papel deve dizer.
      const operationalCosts = ehFaturaDeEntrada ? 0 : calculateOperationalCosts(service);
      const baseValue = totalValue - operationalCosts;

      const operationalDetails = getOperationalCostsDetails(service);

      // A logo do laboratório; sem ela, a do FIVPro.
      const logoPath = logoDoRelatorio();

      // Função para gerar detalhamento da cobrança do serviço base
      const getServiceBillingDetails = () => {
        let details = '';

        // A ENTRADA NÃO É O SERVIÇO. Ela cobra embrião transferido × taxa, e
        // nada mais. Este gerador não sabia do modo e imprimia o detalhamento
        // do modelo de cobrança do serviço embaixo da fatura da entrada.
        if (ehFaturaDeEntrada) {
          const qtd = Number(service.entrada_embrioes) || 0;
          const taxa = qtd > 0 ? totalValue / qtd : 0;
          return `
              <div class="billing-detail">
                <div class="info-row"><span>Embriões transferidos:</span><span>${qtd} unidades</span></div>
                <div class="info-row"><span>Entrada por embrião:</span><span>R$ ${taxa.toFixed(2)}</span></div>
                <div class="calculation-row"><span><strong>Total da entrada:</strong></span><span><strong>${qtd} × R$ ${taxa.toFixed(2)} = R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span></div>
              </div>`;
        }

        switch (service.billing_model) {
          case BILLING_MODELS.POR_TE:
            const valuePerTE = parseFloat(service.value_per_te) || 0;
            const teQuantity = service.te_quantity || 0;
            details = `
              <div class="billing-detail">
                <div class="info-row"><span>Embriões transferidos:</span><span>${teQuantity} unidades</span></div>
                <div class="info-row"><span>Valor por transferência:</span><span>R$ ${valuePerTE.toFixed(2)}</span></div>
                <div class="calculation-row"><span><strong>Subtotal Serviços:</strong></span><span><strong>${teQuantity} × R$ ${valuePerTE.toFixed(2)} = R$ ${baseValue.toLocaleString('pt-BR')}</strong></span></div>
              </div>`;
            break;

          case BILLING_MODELS.POR_PRENHEZ:
            const valuePerPregnancy = parseFloat(service.value_per_pregnancy) || 0;
            const pregnancyQuantity = service.pregnancy_60d_quantity || 0;
            details = `
              <div class="billing-detail">
                <div class="info-row"><span>Embriões transferidos:</span><span>${service.te_quantity || 0} unidades</span></div>
                <div class="info-row"><span>Prenhezes confirmadas:</span><span>${pregnancyQuantity} unidades</span></div>
                <div class="info-row"><span>Valor por prenhez:</span><span>R$ ${valuePerPregnancy.toFixed(2)}</span></div>
                <div class="calculation-row"><span><strong>Subtotal Serviços:</strong></span><span><strong>${pregnancyQuantity} × R$ ${valuePerPregnancy.toFixed(2)} = R$ ${baseValue.toLocaleString('pt-BR')}</strong></span></div>
              </div>`;
            break;

          case BILLING_MODELS.POR_EMBRIAO:
            const valuePerEmbryo = parseFloat(service.value_per_embryo) || 0;
            const embryoQuantity = service.lab_quantity || 0;
            details = `
              <div class="billing-detail">
                <div class="info-row"><span>Embriões produzidos:</span><span>${embryoQuantity} unidades</span></div>
                <div class="info-row"><span>Valor por embrião:</span><span>R$ ${valuePerEmbryo.toFixed(2)}</span></div>
                <div class="calculation-row"><span><strong>Subtotal Serviços:</strong></span><span><strong>${embryoQuantity} × R$ ${valuePerEmbryo.toFixed(2)} = R$ ${baseValue.toLocaleString('pt-BR')}</strong></span></div>
              </div>`;
            break;

          case BILLING_MODELS.DIARIA:
            const dailyValue = parseFloat(service.daily_value) || 0;
            const workDays = service.work_days || 1;
            details = `
              <div class="billing-detail">
                <div class="info-row"><span>Dias de serviço:</span><span>${workDays} dias</span></div>
                <div class="info-row"><span>Valor da diária:</span><span>R$ ${dailyValue.toFixed(2)}</span></div>
                <div class="calculation-row"><span><strong>Subtotal Serviços:</strong></span><span><strong>${workDays} × R$ ${dailyValue.toFixed(2)} = R$ ${baseValue.toLocaleString('pt-BR')}</strong></span></div>
              </div>`;
            break;

          case BILLING_MODELS.PACOTE_COMPLETO:
          case BILLING_MODELS.PERSONALIZADO:
            details = `
              <div class="billing-detail">
                <div class="info-row"><span>Tipo:</span><span>Pacote completo de serviços</span></div>
                <div class="info-row"><span>Serviços inclusos:</span><span>${service.services_offered?.map(rotuloDoServico).join(', ') || 'N/A'}</span></div>
                <div class="calculation-row"><span><strong>Subtotal Serviços:</strong></span><span><strong>R$ ${baseValue.toLocaleString('pt-BR')}</strong></span></div>
              </div>`;
            break;

          default:
            details = `
              <div class="billing-detail">
                <div class="info-row"><span>Modelo de cobrança:</span><span>Personalizado</span></div>
                <div class="calculation-row"><span><strong>Subtotal Serviços:</strong></span><span><strong>R$ ${baseValue.toLocaleString('pt-BR')}</strong></span></div>
              </div>`;
        }

        return details;
      };

      // Função para gerar seção de custos operacionais
      const getOperationalCostsSection = () => {
        if (ehFaturaDeEntrada || operationalDetails.length === 0) return '';

        // Item por item, com etapa, quantidade e preço: o cliente entende o que
        // está pagando, e a equipe lembra depois o que cobrou. Serviço antigo
        // (campos fixos) não tem quantidade e cai na linha de descrição.
        const detalhado = operationalDetails.some(c => c.quantidade !== undefined);
        const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const esc = (t) => String(t ?? '').replace(/[&<>"]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

        return `
          <div>
            <div class="section-title">${detalhado ? 'DETALHAMENTO DE CUSTOS' : 'CUSTOS OPERACIONAIS'}</div>
            <div class="billing-detail operational-costs">
              ${detalhado ? `
                <table style="width:100%;border-collapse:collapse;font-size:12px">
                  <thead>
                    <tr style="text-align:left;border-bottom:1px solid #cbd5e1">
                      <th style="padding:4px">Etapa</th><th style="padding:4px">Item</th>
                      <th style="padding:4px;text-align:right">Qtd</th>
                      <th style="padding:4px;text-align:right">Valor unit.</th>
                      <th style="padding:4px;text-align:right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${operationalDetails.map(cost => `
                      <tr style="border-bottom:1px solid #e2e8f0">
                        <td style="padding:4px">${esc(cost.etapa)}</td>
                        <td style="padding:4px">${esc(cost.label)}</td>
                        <td style="padding:4px;text-align:right">${Number(cost.quantidade).toLocaleString('pt-BR')} ${esc(cost.unidade)}</td>
                        <td style="padding:4px;text-align:right">R$ ${brl(cost.valorUnitario)}</td>
                        <td style="padding:4px;text-align:right">R$ ${brl(cost.value)}</td>
                      </tr>`).join('')}
                  </tbody>
                </table>` : operationalDetails.map(cost => `
                <div class="info-row">
                  <span>${cost.label}:</span>
                  <span>${cost.description}</span>
                  <span>R$ ${cost.value.toFixed(2)}</span>
                </div>
              `).join('')}
              <div class="calculation-row">
                <span><strong>Subtotal Custos:</strong></span>
                <span><strong>R$ ${operationalCosts.toLocaleString('pt-BR')}</strong></span>
              </div>
            </div>
          </div>`;
      };

      const pdfContent = `
  <!DOCTYPE html>
  <html>
  <head>
      <meta charset="utf-8">
      <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.4; }
          .header { margin-bottom: 30px; }
          .header-content {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              min-height: 100px;
          }
          .logo-section {
              flex: 0 0 auto;
              margin-right: 20px;
          }
          .logo {
              height: 80px;
              width: auto;
              max-width: 150px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .title-section {
              flex: 1;
              text-align: center;
              padding-top: 10px;
          }
          .company-name { font-size: 24px; font-weight: bold; color: #1f2937; }
          .lab-dados { font-size: 12px; color: #6b7280; margin-top: 4px; }
          .document-title { font-size: 18px; color: #374151; margin-top: 10px; }
          .invoice-number { margin-top: 5px; color: #6b7280; }

          .section-title { font-weight: bold; color: #1f2937; border-bottom: 2px solid #e5e7eb; padding-bottom: 5px; margin: 25px 0 15px 0; }
          .info-row { display: flex; justify-content: space-between; margin: 8px 0; }
          .billing-detail { background: #f8fafc; padding: 15px; border-radius: 8px; margin: 15px 0; border-left: 4px solid #3b82f6; }
          .operational-costs { border-left-color: #f59e0b; background: #fffbeb; }
          .calculation-row { display: flex; justify-content: space-between; margin: 12px 0; padding-top: 8px; border-top: 1px solid #e5e7eb; }
          .total-value { font-size: 28px; font-weight: bold; color: #059669; text-align: center; margin: 25px 0; padding: 20px; background: #ecfdf5; border-radius: 8px; border: 2px solid #10b981; }
          .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #6b7280; }
          .results-section { background: #fef7f0; padding: 15px; border-radius: 8px; margin: 15px 0; }
          .result-item { display: inline-block; background: #fff; padding: 8px 12px; margin: 4px; border-radius: 20px; border: 1px solid #e5e7eb; }

          ${service.billed ? `
          .billed-watermark {
              position: absolute;
              top: 20px;
              right: 20px;
              background: #10b981;
              color: white;
              padding: 10px 20px;
              border-radius: 25px;
              font-weight: bold;
              font-size: 14px;
              transform: rotate(15deg);
              box-shadow: 0 4px 8px rgba(0,0,0,0.2);
          }` : ''}

          @media print {
              body { margin: 20px; }
              .logo { max-height: 60px; }
          }
      </style>
  </head>
  <body>
      ${service.billed ? '<div class="billed-watermark">FATURADO</div>' : ''}

      <div class="header">
          <div class="header-content">
              <div class="logo-section">
                  <img src="${logoPath}" alt="Logo" class="logo" onerror="this.style.display='none'" />
              </div>
              <div class="title-section">
                  <div class="company-name">${lab?.name || 'Laboratório FIV'}</div>
                  ${dadosDoLaboratorio(lab) ? `<div class="lab-dados">${dadosDoLaboratorio(lab)}</div>` : ''}
                  <div class="document-title">${ehFaturaDeEntrada ? 'FATURA DE ENTRADA' : 'FATURA DE SERVIÇOS'}</div>
                  <div class="invoice-number">Fatura #${String(service.id).padStart(6, '0')}</div>
              </div>
          </div>
      </div>

      <div>
          <div class="section-title">DADOS DO CLIENTE</div>
          <div class="info-row"><span><strong>Cliente:</strong></span><span>${service.client_name}</span></div>
          <div class="info-row"><span><strong>Contato:</strong></span><span>${client?.contact || 'N/A'}</span></div>
          <div class="info-row"><span><strong>Telefone:</strong></span><span>${client?.phone || 'N/A'}</span></div>
      </div>

      <div>
          <div class="section-title">DETALHES DO SERVIÇO</div>
          <div class="info-row"><span><strong>Data do Serviço:</strong></span><span>${new Date(service.date).toLocaleDateString('pt-BR')}</span></div>
          <div class="info-row"><span><strong>Serviços Contratados:</strong></span><span>${service.services_offered?.map(rotuloDoServico).join(', ') || 'N/A'}</span></div>
      </div>

      ${(() => {
        const stageCards = [
          { label: 'Óvulos coletados', value: service.opu_quantity, show: service.opu_done },
          { label: 'Embriões produzidos', value: service.lab_quantity, show: service.lab_done },
          { label: 'Embriões transferidos', value: service.te_quantity, show: service.te_done },
          { label: 'Prenhezes confirmadas', value: service.pregnancy_60d_quantity, show: service.pregnancy_60d_done }
        ].filter(card => card.show && card.value > 0);

        if (stageCards.length > 0) {
          return `
            <div class="results-section">
              <div class="section-title">RESULTADOS TÉCNICOS</div>
              ${stageCards.map(card => `<span class="result-item"><strong>${card.label}:</strong> ${card.value}</span>`).join('')}
            </div>`;
        }
        return '';
      })()}

      ${secaoDosTransferidos(transferidos)}

      <div>
          <div class="section-title">DETALHAMENTO DA COBRANÇA</div>
          ${getServiceBillingDetails()}
          ${(() => {
            const linhas = linhasDeCongelamento(service);
            if (linhas.length === 0) return '';
            // Seção própria, e não emendada no detalhamento do modelo: o
            // cliente precisa ver que isto é uma cobrança À PARTE da prenhez
            // (ou da TE), e não um recálculo dela.
            const total = linhas.reduce((t, l) => t + l.subtotal, 0);
            return `
              <div class="billing-detail">
                <div class="info-row"><span><strong>Embriões congelados</strong></span><span></span></div>
                ${linhas.map(l => `
                  <div class="info-row"><span>${l.rotulo}:</span><span>${l.quantidade} × R$ ${l.preco.toFixed(2)} = R$ ${l.subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span></div>`).join('')}
                <div class="calculation-row"><span><strong>Subtotal Congelamento:</strong></span><span><strong>R$ ${total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span></div>
              </div>`;
          })()}
      </div>

      ${getOperationalCostsSection()}

      <div>
          <div class="section-title">INFORMAÇÕES DE FATURAMENTO</div>
          <div class="info-row"><span><strong>Data de Faturamento:</strong></span><span>${billingDate.toLocaleDateString('pt-BR')}</span></div>
          ${(() => {
            // Parcela única: o vencimento só aparecia na lista "Parcelamento
            // em Nx", que some quando há uma parcela só. A fatura à vista —
            // a maioria — saía sem dizer até quando pagar.
            const parcelas = billingInfo.parcelas || [];
            if (parcelas.length !== 1 || !parcelas[0].vencimento) return '';
            return `<div class="info-row"><span><strong>Vencimento:</strong></span><span>${new Date(parcelas[0].vencimento + 'T12:00:00').toLocaleDateString('pt-BR')}</span></div>`;
          })()}
          ${formaPorExtenso(billingInfo.formaPagamento, billingInfo.formaPagamentoObs)
            ? `<div class="info-row"><span><strong>Forma de Pagamento:</strong></span><span>${formaPorExtenso(billingInfo.formaPagamento, billingInfo.formaPagamentoObs)}</span></div>`
            /* Omite a linha quando não foi informada: "Forma: N/A" num documento
               que vai para o cliente parece campo que alguém esqueceu. */
            : ''}
          <div class="info-row"><span><strong>Modelo de Cobrança:</strong></span><span>${{
            'por_te': 'Por Transferência de Embrião',
            'por_prenhez': 'Por Prenhez Confirmada',
            'diaria': 'Diária de Serviço',
            'pacote_completo': 'Pacote Completo',
            'por_embriao': 'Por Embrião Produzido',
            'personalizado': 'Personalizado'
          }[service.billing_model] || 'N/A'}</span></div>
          ${service.billed ? `<div class="info-row"><span><strong>Status:</strong></span><span>FATURADO</span></div>` : ''}
          ${billingInfo.notes ? `<div class="info-row"><span><strong>Observações:</strong></span><span>${billingInfo.notes}</span></div>` : ''}
      </div>

      ${operationalCosts > 0 ? `
      <div style="background: #f0f9ff; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #0ea5e9;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
              <span><strong>Subtotal Serviços:</strong></span>
              <span><strong>R$ ${baseValue.toLocaleString('pt-BR')}</strong></span>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #e5e7eb;">
              <span><strong>Subtotal Custos:</strong></span>
              <span><strong>R$ ${operationalCosts.toLocaleString('pt-BR')}</strong></span>
          </div>
      </div>
      ` : ''}

      ${(() => {
        const abatido = abatidoNoPDF;
        if (abatido <= 0) return '';
        return `
      <div style="margin: 15px 0; padding: 12px; background: #f8fafc; border-radius: 8px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span>Subtotal do serviço:</span>
              <span>R$ ${(totalValue + abatido).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          </div>
          <div style="display: flex; justify-content: space-between;">
              <span>Entrada já paga:</span>
              <span>− R$ ${abatido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          </div>
      </div>`;
      })()}

      <div class="total-value">
          VALOR TOTAL: R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
      </div>

      ${(() => {
        const parcelas = billingInfo.parcelas || [];
        if (parcelas.length <= 1) return '';
        return `
      <div style="margin: 20px 0;">
          <div class="section-title">Parcelamento em ${parcelas.length}x</div>
          ${parcelas.map(p => `
          <div style="display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #e5e7eb;">
              <span>${p.numero}ª parcela${p.vencimento
                ? ` — vencimento ${new Date(p.vencimento + 'T12:00:00').toLocaleDateString('pt-BR')}`
                : ''}</span>
              <span><strong>R$ ${Number(p.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>
          </div>`).join('')}
      </div>`;
      })()}

      <div class="footer">
          <p>Documento gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
          <p>${lab?.name || 'Laboratório FIV'} - Sistema FIV Pro</p>
          ${responsavelDoLaboratorio(lab) ? `<p>${responsavelDoLaboratorio(lab)}</p>` : ''}
          <br>
          <p><em>Esta fatura representa os serviços executados conforme acordado.</em></p>
      </div>
  </body>
  </html>`;

      printWindow.document.open();
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 2000);

    } catch (error) {
      printWindow.close();
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    }
  };

  const generateServiceGroupPDF = (services, groupTitle, overdueMetrics = null) => {
    if (!services || services.length === 0) return;

    try {
      const logoPath = logoDoRelatorio();

      // Função para gerar card de serviço
      const generateServiceCard = (service, index) => {
        const calculatedValue = calculateServiceValue(service);
        const operationalCosts = calculateOperationalCosts(service);
        const baseValue = calculatedValue - operationalCosts;
        const status = getServiceStatus(service);
        const isOverdue = isPregnancyCheckOverdue(service);
        const daysOverdue = isOverdue ? getDaysOverdue(service) : 0;

        // Estágios completados
        const stageCards = [
          { label: 'Óvulos', value: service.opu_quantity, show: service.opu_done },
          { label: 'Embriões', value: service.lab_quantity, show: service.lab_done },
          { label: 'TEs', value: service.te_quantity, show: service.te_done },
          { label: 'Prenhezes', value: service.pregnancy_60d_quantity, show: service.pregnancy_60d_done }
        ].filter(card => card.show && card.value > 0);

        // Potencial para serviços atrasados "por prenhez"
        let potentialSection = '';
        if (isOverdue && service.billing_model === 'por_prenhez') {
          const teQuantity = service.te_quantity || 0;
          const valuePerPregnancy = parseFloat(service.value_per_pregnancy) || 0;
          const potential = teQuantity * 0.45 * valuePerPregnancy;
          if (potential > 0) {
            potentialSection = `
              <div style="background: #fef2f2; padding: 8px; border-radius: 6px; margin-top: 8px; border-left: 3px solid #ef4444;">
                <div style="font-size: 11px; color: #991b1b;">
                  <strong>Potencial (45%):</strong> ${teQuantity} TEs × 45% × R$ ${valuePerPregnancy.toFixed(2)} =
                  <strong style="color: #dc2626;">R$ ${potential.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong>
                </div>
              </div>`;
          }
        }

        return `
          <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-bottom: 12px; background: #fff; page-break-inside: avoid;">
            ${isOverdue ? `
              <div style="background: #fee2e2; color: #991b1b; padding: 6px; border-radius: 4px; margin-bottom: 8px; font-weight: bold; font-size: 11px; text-align: center;">
                ⚠️ ${daysOverdue} dias atrasado
              </div>` : ''}

            <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
              <div style="flex: 1;">
                <div style="font-weight: bold; font-size: 13px; color: #1f2937;">${service.client_name}</div>
                <div style="font-size: 11px; color: #6b7280; margin-top: 2px;">
                  ${new Date(service.date).toLocaleDateString('pt-BR')} •
                  ${service.services_offered?.map(curtoDoServico).join(' + ') || 'N/A'}
                  ${service.billed && !service.paid ? `
                    <span style="margin-left: 8px; color: #ea580c; font-weight: 600;">
                      • Faturado há ${getDaysSinceBilled(service)} ${getDaysSinceBilled(service) === 1 ? 'dia' : 'dias'}
                    </span>
                  ` : ''}
                </div>
              </div>
              <div style="background: ${
                status === 'Finalizado' ? '#dcfce7' :
                status === 'Aguardando OPU' ? '#fef3c7' :
                status === 'No laboratório' ? '#dbeafe' :
                status === 'Aguardando TE' ? '#e9d5ff' :
                status === 'Aguardando conferência' ? '#fed7aa' : '#f3f4f6'
              }; color: ${
                status === 'Finalizado' ? '#166534' :
                status === 'Aguardando OPU' ? '#854d0e' :
                status === 'No laboratório' ? '#1e40af' :
                status === 'Aguardando TE' ? '#6b21a8' :
                status === 'Aguardando conferência' ? '#9a3412' : '#374151'
              }; padding: 4px 10px; border-radius: 12px; font-size: 10px; font-weight: 600; white-space: nowrap;">
                ${status}
              </div>
            </div>

            ${stageCards.length > 0 ? `
              <div style="background: #fef7f0; padding: 8px; border-radius: 6px; margin-bottom: 8px;">
                <div style="font-size: 11px; color: #6b7280;">
                  ${stageCards.map(card => `<span style="margin-right: 12px;"><strong>${card.label}:</strong> ${card.value}</span>`).join('')}
                </div>
              </div>` : ''}

            ${calculatedValue > 0 ? `
              <div style="background: #f9fafb; padding: 10px; border-radius: 6px;">
                <div style="display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 4px;">
                  <span style="color: #6b7280;">Serviço:</span>
                  <span style="font-weight: 600; color: #1e40af;">R$ ${baseValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
                ${operationalCosts > 0 ? `
                  <div style="display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 4px;">
                    <span style="color: #6b7280;">Custos:</span>
                    <span style="font-weight: 600; color: #ea580c;">+ R$ ${operationalCosts.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div style="border-top: 1px solid #d1d5db; margin: 4px 0;"></div>` : ''}
                <div style="display: flex; justify-content: space-between; font-size: 12px;">
                  <span style="font-weight: bold; color: #374151;">TOTAL:</span>
                  <span style="font-weight: bold; color: #059669;">R$ ${calculatedValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>` : `
              <div style="text-align: center; padding: 10px; color: #9ca3af; font-size: 11px;">Aguardando conclusão</div>`}

            ${potentialSection}

            <div style="display: flex; gap: 6px; margin-top: 8px; font-size: 10px;">
              ${service.billed ? '<span style="background: #dbeafe; color: #1e40af; padding: 3px 8px; border-radius: 10px;">📋 Faturado</span>' : ''}
              ${service.paid ? '<span style="background: #dcfce7; color: #166534; padding: 3px 8px; border-radius: 10px;">✓ Pago</span>' : ''}
            </div>
          </div>`;
      };

      // Calcular totais
      const totalValue = services.reduce((sum, s) => sum + calculateServiceValue(s), 0);
      const totalServices = services.length;

      const pdfContent = `
  <!DOCTYPE html>
  <html>
  <head>
      <meta charset="utf-8">
      <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.4; }
          .header { margin-bottom: 30px; }
          .header-content {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              min-height: 100px;
          }
          .logo-section {
              flex: 0 0 auto;
              margin-right: 20px;
          }
          .logo {
              height: 80px;
              width: auto;
              max-width: 150px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .title-section {
              flex: 1;
              text-align: center;
              padding-top: 10px;
          }
          .company-name { font-size: 24px; font-weight: bold; color: #1f2937; }
          .lab-dados { font-size: 12px; color: #6b7280; margin-top: 4px; }
          .document-title { font-size: 18px; color: #374151; margin-top: 10px; }
          .group-title { margin-top: 5px; color: #6b7280; font-size: 14px; }

          .summary-box {
              background: #f0f9ff;
              padding: 15px;
              border-radius: 8px;
              margin: 20px 0;
              border-left: 4px solid #3b82f6;
          }
          .summary-row {
              display: flex;
              justify-content: space-between;
              margin: 8px 0;
              font-size: 13px;
          }
          .summary-total {
              font-size: 18px;
              font-weight: bold;
              color: #059669;
              margin-top: 10px;
              padding-top: 10px;
              border-top: 2px solid #e5e7eb;
          }

          ${overdueMetrics ? `
          .overdue-metrics {
              background: #fef2f2;
              padding: 15px;
              border-radius: 8px;
              margin: 20px 0;
              border-left: 4px solid #ef4444;
          }
          .overdue-title {
              font-weight: bold;
              color: #991b1b;
              margin-bottom: 10px;
              font-size: 14px;
          }
          .metric-row {
              display: flex;
              justify-content: space-between;
              margin: 6px 0;
              font-size: 12px;
              color: #7f1d1d;
          }` : ''}

          .footer {
              margin-top: 40px;
              text-align: center;
              font-size: 12px;
              color: #6b7280;
              page-break-inside: avoid;
          }

          @media print {
              body { margin: 20px; }
              .logo { max-height: 60px; }
          }
      </style>
  </head>
  <body>
      <div class="header">
          <div class="header-content">
              <div class="logo-section">
                  <img src="${logoPath}" alt="Logo" class="logo" onerror="this.style.display='none'" />
              </div>
              <div class="title-section">
                  <div class="company-name">${currentLab?.name || 'Laboratório FIV'}</div>
                  ${dadosDoLaboratorio(currentLab) ? `<div class="lab-dados">${dadosDoLaboratorio(currentLab)}</div>` : ''}
                  <div class="document-title">RELATÓRIO DE SERVIÇOS</div>
                  <div class="group-title">${groupTitle}</div>
              </div>
          </div>
      </div>

      <div class="summary-box">
          <div class="summary-row">
              <span><strong>Total de Serviços:</strong></span>
              <span>${totalServices}</span>
          </div>
          <div class="summary-row summary-total">
              <span>VALOR TOTAL:</span>
              <span>R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          </div>
      </div>

      ${overdueMetrics ? `
      <div class="overdue-metrics">
          <div class="overdue-title">⚠️ ANÁLISE DE SERVIÇOS ATRASADOS</div>
          <div class="metric-row">
              <span><strong>Total de TEs Atrasadas:</strong></span>
              <span>${overdueMetrics.teCount}</span>
          </div>
          <div class="metric-row">
              <span><strong>Taxa de Sucesso Estimada:</strong></span>
              <span>${(overdueMetrics.successRate * 100).toFixed(0)}%</span>
          </div>
          <div class="metric-row">
              <span><strong>Prenhezes Potenciais:</strong></span>
              <span>${(overdueMetrics.teCount * overdueMetrics.successRate).toFixed(1)}</span>
          </div>
          <div class="metric-row" style="font-size: 14px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #fecaca;">
              <span><strong>POTENCIAL DE FATURAMENTO:</strong></span>
              <span style="font-size: 16px;"><strong>R$ ${overdueMetrics.totalPotential.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></span>
          </div>
      </div>` : ''}

      <div style="margin-top: 20px;">
          ${services.map((service, index) => generateServiceCard(service, index)).join('')}
      </div>

      <div class="footer">
          <p>Relatório gerado em ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}</p>
          <p>${currentLab?.name || 'Laboratório FIV'} - Sistema FIV Pro</p>
          ${responsavelDoLaboratorio(currentLab) ? `<p>${responsavelDoLaboratorio(currentLab)}</p>` : ''}
      </div>
  </body>
  </html>`;

      // Pop-up bloqueado devolve `null`, e o `.document` logo abaixo estourava
      // com "Cannot read properties of null" — mensagem que manda a pessoa
      // procurar bug onde só falta uma permissão do navegador. O openPrint do
      // workflowPDF já avisava certo; estes dois não.
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('O navegador bloqueou a janela do PDF. Permita pop-ups para este site e tente de novo.');
        return;
      }
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 2000);

    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    }
  };

  // O serviço da LISTA, e não o que veio no clique. Quem abre custos de dentro
  // do financeiro do serviço passa o objeto capturado quando aquele modal
  // abriu — e se faturar ou receber mexeu na linha nesse meio-tempo, o
  // `updated_at` dele já estava velho e a gravação era recusada.
  const openCostsModal = (service) => {
    setSelectedCostsService(services.find(s => s.id === service.id) || service);
    setShowCostsModal(true);
  };

  // Função para fechar modal de custos
  const closeCostsModal = () => {
    setShowCostsModal(false);
    setSelectedCostsService(null);
  };

  // Função para salvar custos operacionais
  const handleCostsSave = async (costsData) => {
    if (!canManageServices()) {
      alert('Você não tem permissão para gerenciar custos');
      return;
    }

    setLoading(true);
    try {
      try {
        await updateServiceCosts(selectedCostsService.id, costsData, currentLab.id, selectedCostsService.updated_at);
      } catch (erro) {
        // Carimbo velho não é conflito — a mesma regra do salvamento do
        // workflow. Só regrava se os campos de custo no servidor continuam
        // os que a tela leu ao abrir.
        if (!ehConflito(erro)
            || conflitoAtrapalha(costsData, erro.servidor, selectedCostsService)) {
          throw erro;
        }
        await updateServiceCosts(
          selectedCostsService.id, costsData, currentLab.id, erro.servidor.updated_at);
      }

      // Recarregar dados
      await loadServices();
      closeCostsModal();

      alert('Custos operacionais salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar custos:', error);
      alert('Erro ao salvar custos: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * A observação de quem cobra ("vai pagar dia 15").
   *
   * Sem carimbo de concorrência: escreve uma coluna só, que nenhum gatilho
   * toca, e a última anotação é a que vale. Condicionar ao `updated_at` faria
   * a nota falhar toda vez que um recebimento tivesse mexido na linha.
   */
  const salvarObservacaoDeCobranca = async (service, texto) => {
    const observacao = (texto || '').trim() || null;
    try {
      await updateServiceObservacaoDeCobranca(
        service.id, { observacao_de_cobranca: observacao }, currentLab.id);
      await loadServices();
      return true;
    } catch (error) {
      console.error('Erro ao salvar observação de cobrança:', error);
      alert('Erro ao salvar a observação: ' + error.message);
      return false;
    }
  };

  /**
   * Abre o serviço, opcionalmente JÁ na etapa pedida.
   *
   * `etapa` é um id da corrente ('te', 'opu', 'dg30'...). Vem de quem clica
   * numa pendência: "TE sem lançamento há 24d" tem que cair na aba da TE, e
   * não na etapa que a corrente acha que vem a seguir — são coisas diferentes
   * quando o serviço tem mais de uma etapa parada.
   */
  /** As três portas de um serviço, usadas pelo Dashboard. */
  const abrirCadastro = (service) => openModal('service', service);
  const abrirFinanceiro = (service) => setServicoNoFinanceiro(service);

  const openWorkflowModal = (service, etapa = null) => {
    abertoPeloAssistente.current = false;
    setSelectedService(service);
    setEtapaInicial(etapa);
    setShowWorkflowModal(true);
  };

  const closeWorkflowModal = () => {
    setShowWorkflowModal(false);
    setSelectedService(null);
    setEtapaInicial(null);
  };

  // O estado que o assistente consulta para achar serviços e ver onde o fluxo cabe.
  const contextoDoAssistente = () => ({
    services, clients, lab: currentLab, parcelasPorServico, entradaEmitidaPorServico,
    valorDoServico: calculateServiceValue, foco: servicoEmFoco,
  });

  const falaDoAssistente = (texto, botoes = [], bloco = null) =>
    setConversa(prev => [...prev, { autor: 'assistente', texto, botoes, bloco, em: Date.now() }]);

  /**
   * O que a IA lê de cada mensagem. Resposta que é só um bloco (lista,
   * cartão) não tem texto: vai o que ele mostrou, para "o segundo" ou "esse
   * aí" terem a que se referir.
   */
  const paraAIA = (m) => {
    const b = m.bloco;
    const codigo = (id) => services.find(s => s.id === id)?.service_code;
    const mostrou = !b ? ''
      : b.tipo === 'servicos' ? `[lista: ${(b.ids || []).map(codigo).filter(Boolean).slice(0, 15).join(', ')}]`
      : b.tipo === 'servico' ? `[cartão do serviço ${codigo(b.id) || ''}]`
      : b.tipo === 'cliente' ? `[cartão do cliente ${clients.find(c => c.id === b.id)?.name || ''}]`
      : b.tipo === 'pendencias' ? '[resumo de pendências]'
      : b.tipo === 'prenhas' ? `[receptoras prenhas de: ${(b.grupos || []).map(g => g.client_name).slice(0, 15).join(', ')}]` : '';
    return { autor: m.autor, texto: [m.texto, mostrou].filter(Boolean).join(' ') };
  };

  /** Começa uma conversa nova: a de antes some da tela e do contexto da IA. */
  const novaConversa = () => {
    setConversa([]);
    setServicoEmFoco(null);
    setFila(null);
  };

  const enviarAoAssistente = async (texto) => {
    // Parada há 20 minutos: o pedido de agora não continua o de antes.
    const retomando = !parada(conversa);
    const historico = retomando
      ? conversa.slice(-50).map(paraAIA) : [];
    setConversa(prev => [...(retomando ? prev : encerrar(prev)), { autor: 'pessoa', texto, em: Date.now() }]);
    setAssistentePensando(true);
    try {
      const foco = retomando ? services.find(s => s.id === servicoEmFoco) : null;
      const decisao = await perguntarAoAssistente({
        mensagem: texto, historico, contexto: {
          laboratory_id: currentLab?.id, aba: activeTab,
          servico_em_foco: foco ? `${foco.service_code} · ${foco.client_name}` : null,
        },
      });
      // Animal não vem carregado no FIVSystem: busca pelo brinco/RGD antes de
      // responder, só quando o pedido é sobre um.
      const sobreAnimal = decisao.animal
        && (decisao.fluxo === 'ver_animal' || decisao.fluxo === 'conversa');
      const animais = sobreAnimal
        ? await searchAnimals(decisao.animal, currentLab?.id).catch(() => []) : [];
      // As prenhas também só quando o pedido é sobre elas. Falhou, chega nulo.
      const prenhas = decisao.fluxo === 'listar_prenhas'
        ? await listarDiagnosticosDePrenha({ laboratoryId: currentLab?.id }).catch(() => null) : null;
      // Assunto novo, segundo a IA: a conversa de antes encerra aqui, e a
      // pergunta de agora abre a próxima. O foco velho não vale para ela.
      const assuntoNovo = !retomando || (decisao.assunto_novo && historico.length > 0);
      if (assuntoNovo && retomando) setConversa(prev => encerrar(prev, 1));
      const resposta = responder(decisao, {
        ...contextoDoAssistente(), animais, prenhas, foco: assuntoNovo ? null : servicoEmFoco,
      });
      if (resposta.foco || assuntoNovo) setServicoEmFoco(resposta.foco || null);
      falaDoAssistente(resposta.texto, resposta.botoes, resposta.bloco);
    } catch (erro) {
      falaDoAssistente(erro.message || 'Assistente indisponível agora.');
    } finally {
      setAssistentePensando(false);
    }
  };

  /**
   * Abre o próximo item da fila a partir de `desde`, pulando o que já não
   * cabe (outro alguém emitiu, o serviço mudou). Acabou, avisa e encerra.
   */
  const abrirDaFila = (f, desde) => {
    for (let k = desde; k < f.itens.length; k += 1) {
      const it = f.itens[k];
      const s = services.find(x => x.id === it.serviceId);
      if (!s || porQueNaoCabe(f.alvo, s, contextoDoAssistente(), it.etapa)) continue;
      setFila({ ...f, i: k, aberto: true });
      falaDoAssistente(`${k + 1} de ${f.itens.length}: ${s.service_code} · ${s.client_name}`);
      executarDoAssistente({ fluxo: f.alvo, serviceId: s.id, etapa: it.etapa });
      return;
    }
    setFila(null);
    falaDoAssistente(`Fila concluída (${f.itens.length}).`);
  };

  /**
   * O que cada botão do assistente faz: abre o mesmo modal que o clique na
   * tela abriria. O serviço é procurado de novo na lista atual, para botão de
   * mensagem antiga não abrir uma cópia velha.
   */
  const executarDoAssistente = (acao) => {
    // O clique não vira mensagem nem passa pela IA: abre o que tem de abrir.
    // O serviço clicado vira o foco, e é isso que a próxima pergunta usa.
    const service = acao.serviceId ? services.find(s => s.id === acao.serviceId) : null;
    if (acao.serviceId && !service) {
      falaDoAssistente('Esse serviço não está mais na lista. Me pede de novo?');
      return;
    }
    if (service) setServicoEmFoco(service.id);
    switch (acao.fluxo) {
      case 'ver_servico': {
        const { texto, botoes, bloco } = cartaoDoServico(service);
        falaDoAssistente(texto, botoes, bloco);
        break;
      }
      case 'ver_cliente': {
        const cliente = clients.find(c => c.id === acao.clientId);
        if (!cliente) { falaDoAssistente('Não achei esse cliente.'); break; }
        const { texto, botoes, bloco } = cartaoDoCliente(cliente);
        falaDoAssistente(texto, botoes, bloco);
        break;
      }
      case 'ver_animal': {
        const { texto, botoes } = cartaoDoAnimal(acao.animal);
        falaDoAssistente(texto, botoes);
        break;
      }
      case 'estoque_do_cliente':
        setEstoqueDe(clients.find(c => c.id === acao.clientId) || null);
        break;
      case 'cruzamentos_do_animal':
        // A busca devolve só parte do cadastro; os cruzamentos precisam do tipo.
        fetchAnimal(acao.animal.id)
          .then(a => setCruzamentosDe(a || acao.animal))
          .catch(() => setCruzamentosDe(acao.animal));
        break;
      case 'abrir_etapa':
        openWorkflowModal(service, acao.etapa || null);
        abertoPeloAssistente.current = true;
        break;
      case 'ficha_de_campo':
        gerarFichaDeCampo(service, acao.etapa, { laboratoryId: currentLab?.id })
          ?.catch?.(e => falaDoAssistente(`Não consegui gerar a ficha: ${e.message}`));
        break;
      case 'emitir_entrada': handleChargeEntrada(service); break;
      case 'refaturar_entrada': handleRefaturarEntrada(service); break;
      case 'faturar': handleBillService(service); break;
      case 'refaturar': handleRefaturar(service); break;
      case 'receber': abrirFinanceiro(service); break;
      case 'custos': openCostsModal(service); break;
      case 'editar_servico': openModal('service', service); break;
      case 'novo_servico': openModal('service', null, acao.clientId ? { client_id: acao.clientId } : {}); break;
      case 'novo_cliente': openModal('client'); break;
      case 'editar_cliente': openModal('client', clients.find(c => c.id === acao.clientId) || null); break;
      case 'ir_para_aba': abrirAba(acao.aba); break;
      case 'fila': abrirDaFila({ alvo: acao.alvo, itens: acao.itens || [] }, 0); break;
      case 'fila_proxima': if (fila) abrirDaFila(fila, fila.i); break;
      case 'fila_pular': if (fila) abrirDaFila(fila, fila.i + 1); break;
      case 'fila_parar':
        setFila(null);
        falaDoAssistente('Fila parada.');
        break;
      default: break;
    }
  };

  /**
   * O modal do item da fila fechou (confirmou ou cancelou, tanto faz): o
   * assistente oferece o próximo, sem abrir sozinho — quem decide o ritmo é
   * a pessoa, e ela pode ter fechado para resolver outra coisa.
   */
  const algumModalAberto = showBillingModal || showWorkflowModal || showCostsModal || Boolean(servicoNoFinanceiro);
  useEffect(() => {
    if (!fila?.aberto || algumModalAberto) return;
    const proximo = fila.i + 1;
    if (proximo >= fila.itens.length) {
      setFila(null);
      falaDoAssistente(`Fila concluída (${fila.itens.length}).`);
      return;
    }
    setFila({ ...fila, i: proximo, aberto: false });
    const s = services.find(x => x.id === fila.itens[proximo].serviceId);
    falaDoAssistente(`Próximo: ${s?.service_code || ''} · ${s?.client_name || ''} (${proximo + 1} de ${fila.itens.length})`, [
      { rotulo: 'Abrir', acao: { fluxo: 'fila_proxima' } },
      { rotulo: 'Pular', acao: { fluxo: 'fila_pular' } },
      { rotulo: 'Parar', acao: { fluxo: 'fila_parar' } },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [algumModalAberto, fila?.aberto]);

  /**
   * A cadeia: depois de salvar o workflow, o assistente diz o que ficou
   * liberado. Com ele ligado, isto substitui o confirm da entrada — o botão
   * "Emitir entrada" vem entre as sugestões.
   */
  const sugerirDepoisDoWorkflow = (antes, servicoSalvo) => {
    // O cartão do serviço, e não botões soltos: a linha do tempo já mostra a
    // etapa feita, e o próximo passo vem em destaque nele. Ele lê a lista de
    // agora, então atualiza quando `loadServices` terminar.
    const passos = proximosPassos(servicoSalvo, contextoDoAssistente());
    const feitas = etapasConcluidasAgora(antes, servicoSalvo).map(rotuloDaEtapa);
    falaDoAssistente(
      `${feitas.length ? `${feitas.join(', ')} salva.` : 'Workflow salvo.'}`
        + (passos.length ? '' : ' Nada pendente por agora.'),
      [],
      { tipo: 'servico', id: servicoSalvo.id },
    );
    setServicoEmFoco(servicoSalvo.id);
    if (abertoPeloAssistente.current) abrirAba('assistente');
    else if (activeTab !== 'assistente') setSugestoesNaoVistas(n => n + 1);
  };

  /**
   * A faturinha da entrada, oferecida no momento em que a TE é salva.
   *
   * O convite mora AQUI, e não na aba de TE, porque só depois de
   * `updateServiceWorkflow` a quantidade de transferidos existe no banco —
   * emitir antes cobraria um número que a gravação ainda podia mudar.
   *
   * É convite, não automatismo. Emitir fatura é ato para fora, e `criarFatura`
   * exige servidor de propósito (não é enfileirável, ao contrário do resto do
   * workflow): disparar sozinho faria a cobrança falhar em silêncio no campo,
   * sem internet, exatamente onde a TE é lançada.
   */
  const ofereceEntradaDaTE = (servicoSalvo) => {
    // `null` = a leitura das faturas falhou. Sem saber o que já foi emitido,
    // não se convida a emitir. Ver loadParcelas.
    if (entradaEmitidaPorServico === null) return;
    if (!canManageServices()) return;

    // "Não, obrigado" vale até recarregar. Quem recusa e salva de novo para
    // corrigir uma receptora não quer a mesma pergunta no caminho de volta.
    if (entradaRecusada.current.has(servicoSalvo.id)) return;

    const { deve, quantidade, taxa, valor } = entradaAEmitir(
      servicoSalvo, currentLab, Boolean(entradaEmitidaPorServico[servicoSalvo.id]),
    );
    if (!deve) return;

    const reais = (n) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    const querEmitir = window.confirm(
      `${quantidade} embrião(ões) transferido(s) × R$ ${reais(taxa)} = R$ ${reais(valor)}.\n\n`
      + 'Emitir agora a fatura de entrada deste serviço?\n\n'
      + 'É um ADIANTAMENTO: o que o cliente pagar aqui é abatido do '
      + 'faturamento final deste mesmo serviço. Dá para emitir depois, pelo '
      + 'Financeiro.'
    );
    if (!querEmitir) {
      entradaRecusada.current.add(servicoSalvo.id);
      return;
    }

    handleChargeEntrada({ ...servicoSalvo, entrada_embrioes: quantidade });
  };

  const handleWorkflowSave = async (workflowData, avisos = []) => {
    if (!canManageServices()) {
      alert('Você não tem permissão para gerenciar workflow');
      return;
    }

    setLoading(true);
    try {
      try {
        await updateServiceWorkflow(selectedService.id, workflowData, currentLab.id, selectedService.updated_at);
      } catch (erro) {
        // Carimbo velho não é conflito.
        //
        // `updated_at` marca a linha inteira, e ela se move por fora desta
        // tela: gatilho de espelho do faturamento, migration, gravação de
        // outra aba. Com o workflow aberto há meia hora — o normal de quem
        // lança em campo — o salvamento falhava com "alterado por outra
        // pessoa" sem pessoa nenhuma ter alterado, e sem saída: o alerta não
        // levava a lugar nenhum e o trabalho da tela ficava preso.
        //
        // Só regrava quando os campos DESTE envio continuam iguais aos que a
        // tela leu ao abrir. Divergindo em qualquer um, o conflito é real e
        // sobe como antes.
        if (!ehConflito(erro)
            || conflitoAtrapalha(workflowData, erro.servidor, selectedService)) {
          throw erro;
        }
        await updateServiceWorkflow(
          selectedService.id, workflowData, currentLab.id, erro.servidor.updated_at);
      }

      // O que foi gravado, na mão: `loadServices` é assíncrono e o convite da
      // entrada precisa da quantidade DESTE salvamento, não da lista velha.
      const servicoSalvo = { ...selectedService, ...workflowData };

      // Recarregar dados
      loadServices();
      closeWorkflowModal();

      // Ressalva não é erro: o lançamento está guardado. Mas dizer só
      // "sucesso" quando parte do que a pessoa digitou não subiu é mentir por
      // omissão — e ela sairia do curral achando que os números foram.
      // Com o assistente ligado, o "sucesso" e o convite da entrada viram a
      // mensagem dele, com os próximos passos. A ressalva continua em alerta:
      // é o que a pessoa não pode deixar de ler.
      if (comAssistente) {
        if (avisos.length > 0) alert(`Workflow salvo, com ressalva:\n\n${avisos.join('\n\n')}`);
        sugerirDepoisDoWorkflow(selectedService, servicoSalvo);
        return;
      }
      alert(avisos.length > 0
        ? `Workflow salvo, com ressalva:\n\n${avisos.join('\n\n')}`
        : 'Workflow atualizado com sucesso!');
      ofereceEntradaDaTE(servicoSalvo);
    } catch (error) {
      console.error('Erro ao salvar workflow:', error);
      alert('Erro ao salvar workflow: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Se não tem laboratório, mostra tela de configuração
  if (systemState === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Verificando seu acesso...</p>
        </div>
      </div>
    );
  }

  if (systemState === 'no_access') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-center mb-4">
            <Building className="text-blue-600" size={40} />
          </div>

          <h2 className="text-xl font-semibold text-slate-900 text-center mb-2">
            Nenhum laboratório
          </h2>
          <p className="text-sm text-slate-600 text-center mb-6">
            Sua conta não está vinculada a nenhum laboratório. Crie o primeiro
            para começar — você entra como administrador dele.
          </p>

          <label className="block text-sm font-medium text-slate-700 mb-1">
            Nome do laboratório
          </label>
          <input
            type="text"
            value={firstLabName}
            onChange={(e) => setFirstLabName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createFirstLab(); }}
            placeholder="Ex: RioMinas Embriões"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
          />

          {firstLabError && (
            <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded-lg px-3 py-2 mb-4">
              {MARK.WARN} {firstLabError}
            </div>
          )}

          <button
            onClick={createFirstLab}
            disabled={loading || !firstLabName.trim()}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {loading ? 'Criando...' : 'Criar laboratório'}
          </button>

          <button
            onClick={() => signOut()}
            className="w-full text-slate-500 hover:text-slate-700 text-sm mt-4"
          >
            Sair
          </button>
        </div>
      </div>
    );
  }

  // Bloqueio por licença. Vem depois do carregamento e da checagem de acesso,
  // porque só faz sentido quando já se sabe qual laboratório é. A fila de
  // lançamentos NÃO é tocada: continua no aparelho, esperando liberação.
  if (licenca.estado === 'bloqueado') {
    return (
      <LicencaBloqueada
        motivo={licenca.motivo}
        onTentarNovamente={licenca.tentarNovamente}
        verificando={false}
      />
    );
  }

  // Se chegou aqui, systemState === 'active' - interface principal
  /*
   * A TELA INTEIRA, NOS DOIS SENTIDOS.
   *
   * Na vertical: a página rolava como um documento, e o cabeçalho e as abas
   * subiam junto. Num monitor alto isso desperdiçava altura — sobrava branco
   * embaixo do quadro enquanto era preciso rolar até o topo para trocar de aba.
   * Agora a casca tem a altura exata da janela e UMA faixa rola: o conteúdo. O
   * cabeçalho e as abas ficam onde estão, em qualquer altura de monitor.
   *
   * `dvh` e não `vh` por causa do celular: `100vh` conta a barra de endereço do
   * navegador como se ela não existisse, e a última linha da tela ficava cortada
   * atrás dela. `dvh` é a altura que sobra de verdade.
   *
   * Na horizontal: o conteúdo estava preso em 1280px no meio da tela, então um
   * monitor de 2560px mostrava a mesma tabela de sempre entre duas faixas de
   * branco. A largura agora é a do monitor. O teto de 2400px existe para o
   * ultrawide, onde uma linha de tabela de ponta a ponta obriga a pessoa a
   * varrer a cabeça para ligar o nome do cliente à data da TE.
   */
  return (
    <div className="h-dvh flex flex-col bg-slate-50 overflow-hidden">
      {/* Header */}
      <header className="shrink-0 bg-white shadow-sm border-b">
        <div className="w-full max-w-[2400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              {/* O letreiro já escreve "FIV Pro"; o <h1> ao lado repetia a
                  mesma palavra. O título continua na página — para leitor de
                  tela e para a estrutura do documento —, só que quem carrega o
                  texto agora é o `alt` da imagem. */}
              <h1 className="flex-shrink-0">
                <img src={LOGO_URL()} alt="FIV Pro" className="h-10 w-auto" />
              </h1>
              <p className="text-sm text-slate-600 hidden lg:block">Gestão completa de laboratório</p>
              
              {/* Seletor de Laboratório */}
              <div className="relative">
                <button
                  onClick={() => setShowLabDropdown(!showLabDropdown)}
                  className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Building size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-blue-900">
                    {currentLab?.name || 'Selecionar Lab'}
                  </span>
                  <ChevronDown size={16} className="text-blue-600" />
                </button>
                
                {showLabDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                    <div className="p-2">
                      {userLabs.map(lab => (
                        <button
                          key={lab.id}
                          onClick={() => switchLaboratory(lab)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-slate-50 ${
                            currentLab?.id === lab.id ? 'bg-blue-50 text-blue-900' : 'text-slate-700'
                          }`}
                        >
                          <div className="font-medium">{lab.name}</div>
                          <div className="text-xs text-slate-500">
                            Função: {lab.user_role}
                          </div>
                        </button>
                      ))}
                      {/* Gerenciar laboratórios é o painel da plataforma (/admin), e
                          só existe para quem opera o produto. A configuração do
                          próprio laboratório continua na aba Laboratório. */}
                      {profile?.is_super_admin && (
                        <div className="border-t border-slate-100 mt-2 pt-2">
                          <a
                            href="/admin"
                            className="block w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-50"
                          >
                            ⚙️ Gerenciar Laboratórios
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm text-slate-600">{currentLab?.name}</p>
                <p className="text-xs text-slate-500">
                  {nomeExibido(profile, user)} • {profile?.is_super_admin ? 'super admin' : userRole}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
                title="Sair"
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Os dois avisos são parte do topo fixo: aviso que rola para fora da
          tela é aviso que a pessoa não leu. */}
      <div className="shrink-0">
        <SyncBanner />
        <Conflitos />
      </div>

      {/* Navigation */}
      <nav className="shrink-0 bg-white border-b">
        <div className="w-full max-w-[2400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {/*
              * A ordem separa o que se OPERA do que se CONSULTA.
              *
              * Dashboard, Clientes, Estoque e Financeiro são as telas em que se
              * mexe toda semana. Animais vem depois porque é catálogo: ele se
              * consulta quando se lança uma OPU, e isso acontece dentro do modal
              * de etapas, não na aba. Relatórios, Laboratório e Logs fecham, do
              * mais lido para o mais raro.
              */}
            {[
              // O assistente abre a fila quando está ligado: é a página inicial.
              ...(comAssistente ? [{ id: 'assistente', name: 'Assistente', icon: MessageCircle }] : []),
              { id: 'dashboard', name: 'Dashboard', icon: Activity },
              { id: 'clients', name: 'Clientes', icon: User },
              { id: 'estoque', name: 'Estoque', icon: Snowflake },
              // Aba própria, e não dentro de Estoque: Estoque é o botijão
              // (embrião e sêmen); insumo é a prateleira — catálogo e custo.
              { id: 'insumos', name: 'Insumos', icon: Package },
              { id: 'financial', name: 'Financeiro', icon: DollarSign },
              { id: 'animals', name: 'Animais', icon: PawPrint },
              { id: 'relatorios', name: 'Relatórios', icon: BarChart3 },
              { id: 'lab-settings', name: 'Laboratório', icon: Settings },
              ...(userRole === 'admin' ? [{ id: 'logs', name: 'Logs', icon: Database }] : [])
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => abrirAba(tab.id)}
                  className={`flex items-center space-x-2 py-4 border-b-2 text-sm font-medium whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.name}</span>
                  {tab.id === 'assistente' && sugestoesNaoVistas > 0 && (
                    <span className="ml-1 min-w-[1.25rem] px-1 rounded-full bg-blue-600 text-white text-xs text-center">
                      {sugestoesNaoVistas}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}

      <main className="flex-1 min-h-0 overflow-y-auto">
        <div className="w-full max-w-[2400px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {comAssistente && (
          <Painel id='assistente' activeTab={activeTab} visitadas={visitadas}>
            <AssistenteTab
              mensagens={conversa}
              pensando={assistentePensando}
              onEnviar={enviarAoAssistente}
              onAcao={executarDoAssistente}
              ctx={contextoDoAssistente()}
              onNovaConversa={() => novaConversa()}
              nomeDaPessoa={(profile?.full_name || '').split(' ')[0]}
            />
          </Painel>
        )}

        <Painel id='relatorios' activeTab={activeTab} visitadas={visitadas}>
          <RelatoriosTab currentLab={currentLab} clients={clients}
          services={services} calculateServiceValue={calculateServiceValue}
          podeVerDinheiro={canEdit()} />
        </Painel>

        <Painel id='estoque' activeTab={activeTab} visitadas={visitadas}>
          <EstoqueTab
            currentLab={currentLab}
            userRole={userRole}
            isSuperAdmin={!!profile?.is_super_admin}
            clients={clients}
          />
        </Painel>

        <Painel id='insumos' activeTab={activeTab} visitadas={visitadas}>
          {currentLab && (
            <EstoqueDeInsumos
              laboratoryId={currentLab.id}
              // Mesmo critério do Estoque: só o `viewer` fica de fora.
              podeMexer={!!profile?.is_super_admin || ['admin', 'moderator', 'technician'].includes(userRole)}
            />
          )}
        </Painel>

        <Painel id='logs' activeTab={activeTab} visitadas={visitadas}>
          <LogsTab
            currentLab={currentLab}
            services={services}
            clients={clients}
            servicoDaFatura={servicoDaFatura}
            servicoDaParcela={servicoDaParcela}
            abrirCadastro={abrirCadastro}
            abrirFinanceiro={canEdit() ? abrirFinanceiro : null}
            openWorkflowModal={openWorkflowModal}
            abrirCliente={(client) => openModal('client', client)}
          />
        </Painel>

        <Painel id='lab-settings' activeTab={activeTab} visitadas={visitadas}>
          <LabManagement 
            user={user}
            isSuperAdmin={!!profile?.is_super_admin}
            currentLab={currentLab}
            onLabChange={(lab) => {
              setCurrentLab(lab);
              abrirAba('dashboard');
            }}
            onLabSaved={(lab) => setCurrentLab(atual => ({ ...atual, ...lab }))}
          />
        </Painel>

        <Painel id='dashboard' activeTab={activeTab} visitadas={visitadas}>
          <DashboardTab
            clients={clients}
            currentLab={currentLab}
            calculateServiceValue={calculateServiceValue}
            services={services}
            getServiceStatus={getServiceStatus}
            getFinancialStatusIcons={getFinancialStatusIcons}
            getStageCards={getStageCards}
            openWorkflowModal={openWorkflowModal}
            abrirCadastro={abrirCadastro}
            abrirFinanceiro={abrirFinanceiro}
            novoServico={canManageServices() ? (inicial) => openModal('service', null, inicial) : null}
            excluirServico={canEdit() ? excluirServico : null}
            servicoEmDestaque={servicoEmDestaque}
            // Financeiro é de moderador para cima. Técnico lança etapa e edita
            // cadastro; faturar e receber não são trabalho dele.
            podeFinanceiro={canEdit()}
            openModal={openModal}
            parcelasPorServico={parcelasPorServico}
            entradaPagaPorServico={entradaPagaPorServico}
            entradaEmitidaPorServico={entradaEmitidaPorServico}
            generateServiceGroupPDF={generateServiceGroupPDF}
          />
        </Painel>

        <Painel id='clients' activeTab={activeTab} visitadas={visitadas}>
          <ClientsTab
            clients={clients}
            services={services}
            canEdit={canEdit}
            openModal={openModal}
            handleDelete={handleDelete}
            getServiceStatus={getServiceStatus}
            currentLab={currentLab}
          />
        </Painel>
        <Painel id='animals' activeTab={activeTab} visitadas={visitadas}>
          <AnimalsTab
            laboratoryId={currentLab?.id}
            clients={clients}
            canEdit={canEdit}
          />
        </Painel>

        <Painel id='financial' activeTab={activeTab} visitadas={visitadas}>
          <FinancialTab
            entrada={entradas.financial || 0}
            services={services}
            currentLab={currentLab}
            calculateServiceValue={calculateServiceValue}
            getServiceStatus={getServiceStatus}
            canManageServices={canManageServices}
            canEdit={canEdit}
            openCostsModal={openCostsModal}
            openModal={openModal}
            handleBillService={handleBillService}
            handleChargeEntrada={handleChargeEntrada}
            entradaPagaPorServico={entradaPagaPorServico}
            entradaEmitidaPorServico={entradaEmitidaPorServico}
            generateBillingPDF={generateBillingPDF}
            clients={clients}
            handleMarkAsPaid={handleMarkAsPaid}
            handleTogglePago={handleTogglePago}
            handleRefaturar={handleRefaturar}
        handleRefaturarEntrada={handleRefaturarEntrada}
            parcelasPorServico={parcelasPorServico}
            contaPorServico={contaPorServico}
            versaoDasParcelas={versaoDasParcelas}
            formaPorServico={formaPorServico}
            loading={loading}
            generateServiceGroupPDF={generateServiceGroupPDF}
            salvarObservacaoDeCobranca={salvarObservacaoDeCobranca}
          />
        </Painel>
        </div>
      </main>

      <ConfirmarExclusaoModal
        show={!!servicoParaExcluir}
        titulo="serviço"
        codigo={servicoParaExcluir?.service_code || ''}
        rotuloDoCodigo="o código do serviço"
        descricao={exclusao.descricao}
        aviso={exclusao.aviso}
        levaTambem={exclusao.levaTambem}
        loading={loading}
        onConfirmar={confirmarExclusaoDeServico}
        onClose={() => setServicoParaExcluir(null)}
      />

      {/* Modal */}
      <ServiceModal
        showModal={showModal}
        modalType={modalType}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        clients={clients}
        services={services}
        loading={loading}
        onSave={handleSave}
        onClose={closeModal}
        onNovoCliente={canEdit() ? abrirNovaFazendaDoServico : null}
        voltaAoServico={!!servicoEmEspera}
        getServiceStatus={getServiceStatus}
        taxaDeEntradaDoLab={taxaDeEntrada(null, currentLab)}
        laboratoryId={currentLab?.id}
        entradaEmitida={selectedItem?.id ? entradaEmitidaPorServico?.[selectedItem.id] || null : null}
      />

      {/* Modal de Workflow - NOVO */}
      {/* O dinheiro de um serviço. Recebe tudo o que a aba Financeiro recebia,
          porque é para onde as ações dela vão. */}
      <FinanceiroDoServicoModal
        show={!!servicoNoFinanceiro}
        // Da lista, para acompanhar o que muda com o modal aberto (custos,
        // faturamento, observação). O estado guarda só QUAL serviço.
        service={services.find(s => s.id === servicoNoFinanceiro?.id) || servicoNoFinanceiro}
        onClose={() => setServicoNoFinanceiro(null)}
        currentLab={currentLab}
        clients={clients}
        services={services}
        loading={loading}
        canEdit={canEdit}
        canManageServices={canManageServices}
        calculateServiceValue={calculateServiceValue}
        conta={contaPorServico[servicoNoFinanceiro?.id] || {}}
        parcelas={parcelasPorServico[servicoNoFinanceiro?.id] || []}
        entradaPaga={entradaPagaPorServico[servicoNoFinanceiro?.id] || 0}
        entradaEmitida={entradaEmitidaPorServico?.[servicoNoFinanceiro?.id] || null}
        leituraFalhou={entradaEmitidaPorServico === null}
        forma={formaPorServico[servicoNoFinanceiro?.id]
          ? formaPorExtenso(formaPorServico[servicoNoFinanceiro.id].forma,
                           formaPorServico[servicoNoFinanceiro.id].obs)
          : null}
        openCostsModal={openCostsModal}
        handleBillService={handleBillService}
        handleChargeEntrada={handleChargeEntrada}
        handleMarkAsPaid={handleMarkAsPaid}
        handleTogglePago={handleTogglePago}
        handleRefaturar={handleRefaturar}
            handleRefaturarEntrada={handleRefaturarEntrada}
        generateBillingPDF={generateBillingPDF}
        salvarObservacaoDeCobranca={salvarObservacaoDeCobranca}
      />

      <WorkflowModal
        clients={clients}
        services={services}
        laboratory={currentLab}
        entradaEmitida={entradaEmitidaPorServico?.[selectedService?.id] || null}
        showModal={showWorkflowModal}
        etapaInicial={etapaInicial}
        service={selectedService}
        onSave={handleWorkflowSave}
        onClose={closeWorkflowModal}
        loading={loading}
      />
      {recebendo && (
        <RecebimentoModal
          show
          parcela={recebendo.parcela}
          servico={recebendo.service}
          laboratoryId={currentLab?.id}
          onFechar={() => setRecebendo(null)}
          onMudou={() => { loadServices(); loadParcelas(); }}
        />
      )}

      <BillingModal
        showModal={showBillingModal}
        service={selectedBillingService}
        onConfirm={handleBillingConfirm}
        onClose={() => setShowBillingModal(false)}
        loading={loading}
        calculateServiceValue={calculateServiceValue}
        currentLab={currentLab}
        clients={clients}
        modo={modoCobranca}
        entradaPaga={entradaPagaPorServico[selectedBillingService?.id] || 0}
        generateBillingPDF={generateBillingPDF} // Nova prop adicionada
      />

      <OperationalCostsModal
        showModal={showCostsModal}
        service={selectedCostsService}
        precoPorKmPadrao={precosParaServicoNovo(currentLab).price_per_km}
        laboratoryId={currentLab?.id}
        onSave={handleCostsSave}
        onClose={closeCostsModal}
        loading={loading}
      />

      {estoqueDe && (
        <JanelaModal chave="estoque-do-cliente" titulo={`Estoque · ${estoqueDe.name}`}
          onFechar={() => setEstoqueDe(null)} largura="sm:max-w-3xl">
          <EstoqueDoCliente client={estoqueDe} laboratoryId={currentLab?.id} laboratorio={currentLab} />
        </JanelaModal>
      )}

      {cruzamentosDe && (
        <CruzamentosDoAnimal animal={cruzamentosDe} laboratoryId={currentLab?.id}
          onClose={() => setCruzamentosDe(null)} />
      )}
    </div>
  );
};

export default FIVSystem;